import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import {
  createJobV1JobsPost,
  createScheduleV1JobsJobIdSchedulesPost,
  getListJobsV1JobsGetQueryKey,
  getListRunsV1RunsGetQueryKey,
  getListSchedulesV1JobsJobIdSchedulesGetQueryKey,
  updateJobV1JobsJobIdPatch,
  useCreateScheduleV1JobsJobIdSchedulesPost,
  useDeleteJobV1JobsJobIdDelete,
  useDeleteScheduleV1SchedulesScheduleIdDelete,
  useImportJobsV1JobsImportPost,
  useListBrandsV1BrandsGet,
  useListJobKnobsV1JobsKnobsGet,
  useListJobsV1JobsGet,
  useListSchedulesV1JobsJobIdSchedulesGet,
  useRunNowV1JobsJobIdRunsPost,
  useStoresV1StoresGet,
  useUpdateJobV1JobsJobIdPatch,
  useUpdateScheduleV1SchedulesScheduleIdPatch,
} from "../api/generated/qcsControlPlane";
import { Flow, FlowCategory, KnobGroup, KnobTier, type JobImportResult, type JobKnobDescriptor, type JobLastRun, type Store } from "../api/generated/model";
import { ApiError } from "../api/mutator";
import { ChevronDown, RotateCcw, X } from "lucide-react";
import type { InputKind, InputValue } from "@akaike/ui";
import {
  type Job,
  type JobInputs,
  type Platform,
  CATEGORY_LABEL,
  FLOW_CATEGORY,
  MANAGED_FLOWS,
  SEEDED_FLOWS,
  ago,
  flowLabel,
  fmtTime,
  platformLabel,
  statusTone,
} from "../api/labels";
import { platformHasLanes, platformKeysFor } from "../api/platforms";
import { AnimatedNumber } from "../lib/motion";
import { BboxMapPicker, INDIA_BBOX_FORM } from "../components/BboxMapPicker";
import { IconPlus } from "../components/icons";
import { RolePill, TierPills } from "../components/tiers";
import { useToast } from "../components/toast";
import { Badge, Button, Dot, EmptyState, ErrorState, FormField, FormSection, Grid, Input, KindInput, NativeSelect as SharedSelect, PageHeader, Panel, Row, SegmentedControl, Skeleton, SkeletonRegion, Stack, Switch, Tag, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@akaike/ui";
import { toneVariant } from "../lib/badge-tone";
import { NativeSelect as Select, Stat } from "../components/foundation";
import { useCanWrite } from "../auth/me";

// Scope controls stay specific to each flow. The descriptor owns every other knob.
const SCOPE_KNOBS = new Set([
  "force_refresh", "include_bundles", "include_inactive", "bbox", "mode",
  "close_stores", "exclude_combo", "use_cache", "sku_source",
  "skip_existing", "recheck_absent", "tracked_skus_only", "search_first",
]);

// Scope knobs the form has a control for, per flow. Save rewrites only these; any
// other stored scope knob (cart force_refresh, taxonomy mode) passes through as is.
const FLOW_SCOPE_KNOBS: Record<Flow, readonly string[]> = {
  cart_inventory: ["include_bundles", "include_inactive", "sku_source"],
  store_sku_map: ["include_bundles", "sku_source"],
  merchant_sweep: ["force_refresh", "bbox", "mode", "close_stores"],
  brand_sweep: ["mode"],
  product_match: ["exclude_combo", "use_cache"],
  shelf_id_resolve: ["search_first", "skip_existing", "recheck_absent", "tracked_skus_only"],
  taxonomy_classify: [],
  search_rank: [],
  category_rank: [],
  availability_build: [],
  availability_rebuild: [],
  availability_backfill: [],
};

const KNOB_GROUPS = Object.values(KnobGroup);
function knobGroupLabel(group: KnobGroup): string {
  switch (group) {
    case KnobGroup.pool: return "Proxy pool";
    case KnobGroup.pacing: return "Pacing";
    case KnobGroup.scope: return "Scope";
    case KnobGroup.flow: return "Flow";
  }
}
const CASCADE_KEYS = [
  "coarse_step", "mid_step", "dense_step", "mid_half_width", "dense_half_width",
] as const;
const DEGREE_KNOBS = new Set<string>([
  ...CASCADE_KEYS, "update_margin", "repair_margin",
]);

// A degree is about 111.32 km of latitude at Indian latitudes.
const KM_PER_DEG = 111.32;
function degToDistanceLabel(deg: number): string {
  const km = deg * KM_PER_DEG;
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(km < 10 ? 2 : 1)} km`;
}

// Shared palette for the cascade preview (canvas + its legend).
const CASCADE_COLORS = {
  coarse: "#6366f1", mid: "#0ea5e9", dense: "#f59e0b", overlap: "#ef4444",
} as const;
const DENSE_DOT_EDGE = "#7a3d00";

const MERCHANT_MODES: readonly { value: string; label: string; hint: string }[] = [
  { value: "full", label: "Full", hint: "Sweep the whole region from scratch. Detects both opened and closed stores." },
  { value: "update", label: "Update", hint: "Re-sweep only around existing active coverage (+ margin). Cheaper refresh; still closes absent stores." },
  { value: "repair", label: "Repair", hint: "Re-sweep only around stores that drifted (from cart inventory). Closure disabled; no-ops when nothing drifted." },
];
const BRAND_MODES: readonly { value: string; label: string; hint: string }[] = [
  { value: "full", label: "Full", hint: "Search every active store AND retire missing SKUs. Authoritative; run periodically (e.g. weekly)." },
  { value: "sampled", label: "Sampled", hint: "Search a rotating random store sample; fast + additive (removals are deferred to the rolling window / a Full run). Good for frequent refresh." },
];

function displayKnobDefault(value: JobKnobDescriptor["default"]): string {
  if (value === null) return "none";
  if (typeof value === "boolean") return value ? "on" : "off";
  return Array.isArray(value) ? value.join(", ") || "none" : String(value);
}

function descriptorValue(value: unknown): InputValue {
  if (value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value;
  if (Array.isArray(value)) return value as string[];
  if (typeof value === "object") return value as Record<string, string | number | boolean | string[] | null>;
  return null;
}

function knobValidationError(descriptor: JobKnobDescriptor, value: InputValue): string | null {
  if (value === null || value === "") return null;
  const kind = descriptor.kind;
  if (kind.kind !== "int" && kind.kind !== "float") return null;
  if (typeof value !== "number" || !Number.isFinite(value)) return "Enter a number.";
  if (kind.kind === "int" && !Number.isInteger(value)) return "Enter a whole number.";
  if (kind.min != null && (value < kind.min || (kind.kind === "float" && kind.min_exclusive && value === kind.min)))
    return `Must be ${kind.kind === "float" && kind.min_exclusive ? "greater than" : "at least"} ${kind.min}.`;
  if (kind.max != null && (value > kind.max || (kind.kind === "float" && kind.max_exclusive && value === kind.max)))
    return `Must be ${kind.kind === "float" && kind.max_exclusive ? "less than" : "at most"} ${kind.max}.`;
  return null;
}

function DescriptorKnobInput({ descriptor, value, error, onChange }: {
  descriptor: JobKnobDescriptor;
  value: InputValue;
  error?: string;
  onChange: (value: InputValue) => void;
}) {
  const id = `job-knob-${descriptor.name}`;
  const defaultText = displayKnobDefault(descriptor.default);
  const effective = typeof value === "number" ? value : descriptor.default;
  const distance = DEGREE_KNOBS.has(descriptor.name)
    && typeof effective === "number" && Number.isFinite(effective)
    ? degToDistanceLabel(effective) : null;
  return (
    <FormField
      label={descriptor.label}
      htmlFor={id}
      error={error}
      hint={
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="cursor-help text-muted-foreground">
                Default {defaultText} ({descriptor.source}){distance && `; ~ ${distance}`}
              </span>
            </TooltipTrigger>
            <TooltipContent>{descriptor.help}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      }
      action={value !== null && value !== "" ? (
        <Button type="button" variant="ghost" size="icon-sm" aria-label={`Reset ${descriptor.label}`} title="Reset to default" onClick={() => onChange(null)}>
          <RotateCcw className="size-3" />
        </Button>
      ) : undefined}
    >
      <KindInput
        id={id}
        kind={descriptor.kind as InputKind}
        value={value}
        onChange={onChange}
        placeholder={defaultText}
        compact
        aria-invalid={Boolean(error)}
      />
    </FormField>
  );
}

// -- Geo-cascade visual preview -----------------------------------------------
// Points of a globally-anchored hex lattice (rows spaced step*sqrt(3)/2, odd rows
// offset by step/2) inside a lat/lon bbox, a faithful port of core/geo/grid.py's
// _hex_grid_in_bbox. `cap` bounds the count so extreme knob values can't hang the
// draw loop; `capped` signals the preview was truncated.
function hexPointsInBbox(
  minLat: number,
  minLon: number,
  maxLat: number,
  maxLon: number,
  step: number,
  cap: number,
): { pts: [number, number][]; capped: boolean } {
  const pts: [number, number][] = [];
  const rowDy = step * (Math.sqrt(3) / 2);
  const jMin = Math.ceil(minLat / rowDy - 1e-9);
  const jMax = Math.floor(maxLat / rowDy + 1e-9);
  for (let j = jMin; j <= jMax; j++) {
    const lat = j * rowDy;
    const offset = (j & 1) * (step / 2);
    const iMin = Math.ceil((minLon - offset) / step - 1e-9);
    const iMax = Math.floor((maxLon - offset) / step + 1e-9);
    for (let i = iMin; i <= iMax; i++) {
      if (pts.length >= cap) return { pts, capped: true };
      pts.push([lat, offset + i * step]);
    }
  }
  return { pts, capped: false };
}

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// A single interactive schematic of the whole coarse -> mid -> dense hex cascade
// for the current knob values. Two *diagonally* adjacent serviceable coarse cells
// (A, B) each get a square +/- half-width zone densified to the mid step; the same
// happens one level deeper around two mid cells (A', B'). Every probe point is
// drawn with concentric rings: coarse (outer) / mid / dense (inner dot), so a
// point probed at several levels reads as nested rings, and the red fills mark
// where neighbouring refine zones overlap (the same ground re-probed). Geometry
// mirrors core/geo/grid.py (globally-anchored hex lattice + square bbox densify).
// Scroll or use the buttons to zoom; drag to pan. Redraws live and on resize.
type ProbeFlags = { coarse: boolean; mid: boolean; dense: boolean };

function CascadePreview({
  coarseStep,
  midStep,
  denseStep,
  midHalf,
  denseHalf,
}: {
  coarseStep: number;
  midStep: number;
  denseStep: number;
  midHalf: number;
  denseHalf: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  // User view transform over the fitted scale: zoom multiplier + world-space pan.
  const view = useRef({ zoom: 1, panLon: 0, panLat: 0 });
  // Latest draw metrics, so pointer/wheel handlers can map screen <-> world.
  const metrics = useRef({ fitScale: 1, cx: 0, cy: 0, cssW: 1, plotH: 1 });

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const cssW = Math.max(260, Math.floor(wrap.clientWidth));
    const cssH = Math.round(cssW * 0.5);
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);

    const css = getComputedStyle(document.documentElement);
    const ink = css.getPropertyValue("--ink").trim() || "#374151";
    const line = css.getPropertyValue("--line").trim() || "#d1d5db";
    const { coarse: C, mid: M, dense: D, overlap: O } = CASCADE_COLORS;
    const SQRT3_2 = Math.sqrt(3) / 2;

    // Serviceable coarse pair: diagonal hex neighbours (up + right), one step apart.
    const rowDyC = coarseStep * SQRT3_2;
    const A: [number, number] = [0, 0];
    const B: [number, number] = [rowDyC, coarseStep / 2];

    // Collect probe points with the levels that hit each: coarse over the whole
    // view; mid within the two mid boxes; dense within the two dense boxes. Because
    // the lattices are nested, a coarse point is also mid/dense: flags accumulate.
    const flags = new Map<string, ProbeFlags>();
    const mark = (lat: number, lon: number, level: keyof ProbeFlags) => {
      const key = `${lat.toFixed(6)},${lon.toFixed(6)}`;
      const f = flags.get(key) ?? { coarse: false, mid: false, dense: false };
      f[level] = true;
      flags.set(key, f);
    };

    // Mid boxes around A and B; find the mid cells inside them (mid-probed).
    const midPts: [number, number][] = [];
    for (const p of [A, B]) {
      const g = hexPointsInBbox(p[0] - midHalf, p[1] - midHalf, p[0] + midHalf, p[1] + midHalf, midStep, 8000);
      for (const [lat, lon] of g.pts) {
        mark(lat, lon, "mid");
        midPts.push([lat, lon]);
      }
    }

    // Dense pair, a mid cell in A's clean lower-left corner + its diagonal neighbour
    // (kept away from the A/B overlap so the dense level reads on its own).
    const tgt: [number, number] = [-midHalf * 0.5, -midHalf * 0.5];
    let Ap: [number, number] = tgt;
    let best = Infinity;
    for (const [lat, lon] of midPts) {
      const d = (lat - tgt[0]) ** 2 + (lon - tgt[1]) ** 2;
      if (d < best) {
        best = d;
        Ap = [lat, lon];
      }
    }
    const rowDyM = midStep * SQRT3_2;
    const Bp: [number, number] = [Ap[0] + rowDyM, Ap[1] + midStep / 2];
    for (const p of [Ap, Bp]) {
      const g = hexPointsInBbox(p[0] - denseHalf, p[1] - denseHalf, p[0] + denseHalf, p[1] + denseHalf, denseStep, 8000);
      for (const [lat, lon] of g.pts) mark(lat, lon, "dense");
    }

    // Fit bounds = union of the two mid boxes and the two dense boxes.
    const minLat = Math.min(A[0] - midHalf, B[0] - midHalf, Ap[0] - denseHalf, Bp[0] - denseHalf);
    const maxLat = Math.max(A[0] + midHalf, B[0] + midHalf, Ap[0] + denseHalf, Bp[0] + denseHalf);
    const minLon = Math.min(A[1] - midHalf, B[1] - midHalf, Ap[1] - denseHalf, Bp[1] - denseHalf);
    const maxLon = Math.max(A[1] + midHalf, B[1] + midHalf, Ap[1] + denseHalf, Bp[1] + denseHalf);

    // Coarse lattice over the fit bounds (coarse probes the whole region).
    const coarse = hexPointsInBbox(minLat, minLon, maxLat, maxLon, coarseStep, 6000);
    for (const [lat, lon] of coarse.pts) mark(lat, lon, "coarse");

    // Transform: fit at zoom 1, then apply the user's zoom + pan.
    const dataW = maxLon - minLon || 1e-6;
    const dataH = maxLat - minLat || 1e-6;
    const plotH = cssH - 20; // reserve a strip for the scale bar
    const pad = 0.1;
    const fitScale = Math.min((cssW * (1 - 2 * pad)) / dataW, (plotH * (1 - 2 * pad)) / dataH);
    const { zoom, panLon, panLat } = view.current;
    const scale = fitScale * zoom;
    const cx = (minLon + maxLon) / 2 + panLon;
    const cy = (minLat + maxLat) / 2 + panLat;
    metrics.current = {
      fitScale,
      cx: (minLon + maxLon) / 2,
      cy: (minLat + maxLat) / 2,
      cssW,
      plotH,
    };
    const sx = (lon: number) => cssW / 2 + (lon - cx) * scale;
    const sy = (lat: number) => plotH / 2 - (lat - cy) * scale;

    const box = (c: [number, number], half: number, color: string, dash: boolean) => {
      const x = sx(c[1] - half);
      const y = sy(c[0] + half);
      const wh = 2 * half * scale;
      ctx.fillStyle = hexToRgba(color, 0.05);
      ctx.fillRect(x, y, wh, wh);
      ctx.save();
      ctx.setLineDash(dash ? [5, 4] : []);
      ctx.strokeStyle = hexToRgba(color, 0.85);
      ctx.lineWidth = 1.25;
      ctx.strokeRect(x, y, wh, wh);
      ctx.restore();
    };
    const overlap = (c0: [number, number], c1: [number, number], half: number, alpha: number) => {
      const latLo = Math.max(c0[0], c1[0]) - half;
      const latHi = Math.min(c0[0], c1[0]) + half;
      const lonLo = Math.max(c0[1], c1[1]) - half;
      const lonHi = Math.min(c0[1], c1[1]) + half;
      if (latHi <= latLo + 1e-12 || lonHi <= lonLo + 1e-12) return;
      ctx.fillStyle = hexToRgba(O, alpha);
      ctx.fillRect(sx(lonLo), sy(latHi), (lonHi - lonLo) * scale, (latHi - latLo) * scale);
    };

    // Refine-zone fills + overlaps (drawn under the dots).
    box(A, midHalf, M, false);
    box(B, midHalf, M, true);
    overlap(A, B, midHalf, 0.14);
    box(Ap, denseHalf, D, false);
    box(Bp, denseHalf, D, true);
    overlap(Ap, Bp, denseHalf, 0.14);

    // Probe points as concentric rings: coarse (outer) / mid / dense (inner dot).
    for (const [key, f] of flags) {
      const [lat, lon] = key.split(",").map(Number);
      const px = sx(lon);
      const py = sy(lat);
      if (px < -8 || px > cssW + 8 || py < -8 || py > plotH + 8) continue;
      if (f.coarse) {
        ctx.beginPath();
        ctx.arc(px, py, 5, 0, Math.PI * 2);
        ctx.strokeStyle = C;
        ctx.lineWidth = 1.4;
        ctx.stroke();
      }
      if (f.mid) {
        ctx.beginPath();
        ctx.arc(px, py, 3.2, 0, Math.PI * 2);
        ctx.strokeStyle = M;
        ctx.lineWidth = 1.3;
        ctx.stroke();
      }
      if (f.dense) {
        ctx.beginPath();
        ctx.arc(px, py, 2.1, 0, Math.PI * 2);
        ctx.fillStyle = D;
        ctx.fill();
        ctx.lineWidth = 0.9;
        ctx.strokeStyle = DENSE_DOT_EDGE;
        ctx.stroke();
      }
    }

    // Picked-cell labels: the coarse pair that seeds the mid boxes (A, B) and the
    // mid pair that seeds the dense boxes (A', B'). A' and B' are pushed to opposite
    // outer sides since they sit only one mid-step apart.
    ctx.font = "bold 11px ui-sans-serif, system-ui";
    ctx.fillStyle = ink;
    ctx.textBaseline = "bottom";
    ctx.textAlign = "center";
    ctx.fillText("A", sx(A[1]), sy(A[0]) - 8);
    ctx.fillText("B", sx(B[1]), sy(B[0]) - 8);
    ctx.fillStyle = DENSE_DOT_EDGE; // dark amber: reads as "dense" but stays legible on the fill
    ctx.textAlign = "right";
    ctx.textBaseline = "top";
    ctx.fillText("A'", sx(Ap[1]) - 7, sy(Ap[0]) + 5);
    ctx.textAlign = "left";
    ctx.textBaseline = "bottom";
    ctx.fillText("B'", sx(Bp[1]) + 7, sy(Bp[0]) - 5);
    ctx.textAlign = "start";
    ctx.textBaseline = "alphabetic";

    // Scale bar (bottom-left), tracks the current zoom.
    const kmPerPx = KM_PER_DEG / scale;
    const targetKm = 80 * kmPerPx;
    const nice = [0.05, 0.1, 0.2, 0.5, 1, 2, 5, 10, 20, 50, 100].reduce(
      (acc, v) => (v <= targetKm ? v : acc),
      0.05,
    );
    const barPx = (nice / KM_PER_DEG) * scale;
    const bx = 10;
    const by = cssH - 8;
    ctx.strokeStyle = ink;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(bx, by);
    ctx.lineTo(bx + barPx, by);
    ctx.moveTo(bx, by - 3);
    ctx.lineTo(bx, by + 3);
    ctx.moveTo(bx + barPx, by - 3);
    ctx.lineTo(bx + barPx, by + 3);
    ctx.stroke();
    ctx.fillStyle = ink;
    ctx.font = "10px ui-monospace, monospace";
    ctx.textBaseline = "middle";
    ctx.fillText(nice < 1 ? `${Math.round(nice * 1000)} m` : `${nice} km`, bx + barPx + 6, by);

    ctx.strokeStyle = line;
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, cssW - 1, cssH - 1);
  }, [coarseStep, midStep, denseStep, midHalf, denseHalf]);

  // Reset the view whenever the knobs change (fit bounds shift), then draw. Wire
  // resize + wheel-zoom (to cursor) + drag-pan.
  useEffect(() => {
    view.current = { zoom: 1, panLon: 0, panLat: 0 };
    draw();
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;

    const ro = new ResizeObserver(() => draw());
    ro.observe(wrap);

    const zoomAt = (mx: number, my: number, factor: number) => {
      const m = metrics.current;
      const v = view.current;
      const scale = m.fitScale * v.zoom;
      const wlon = (mx - m.cssW / 2) / scale + m.cx + v.panLon;
      const wlat = (m.plotH / 2 - my) / scale + m.cy + v.panLat;
      const nextZoom = Math.min(10, Math.max(0.5, v.zoom * factor));
      const nextScale = m.fitScale * nextZoom;
      v.zoom = nextZoom;
      v.panLon = wlon - (mx - m.cssW / 2) / nextScale - m.cx;
      v.panLat = wlat - (m.plotH / 2 - my) / nextScale - m.cy;
      draw();
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      zoomAt(e.clientX - rect.left, e.clientY - rect.top, e.deltaY < 0 ? 1.15 : 1 / 1.15);
    };
    canvas.addEventListener("wheel", onWheel, { passive: false });

    let dragging = false;
    let lastX = 0;
    let lastY = 0;
    const onDown = (e: PointerEvent) => {
      dragging = true;
      lastX = e.clientX;
      lastY = e.clientY;
      canvas.setPointerCapture(e.pointerId);
      canvas.style.cursor = "grabbing";
    };
    const onMove = (e: PointerEvent) => {
      if (!dragging) return;
      const m = metrics.current;
      const scale = m.fitScale * view.current.zoom;
      view.current.panLon -= (e.clientX - lastX) / scale;
      view.current.panLat += (e.clientY - lastY) / scale;
      lastX = e.clientX;
      lastY = e.clientY;
      draw();
    };
    const onUp = (e: PointerEvent) => {
      dragging = false;
      if (canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
      canvas.style.cursor = "grab";
    };
    canvas.addEventListener("pointerdown", onDown);
    canvas.addEventListener("pointermove", onMove);
    canvas.addEventListener("pointerup", onUp);
    canvas.style.cursor = "grab";

    return () => {
      ro.disconnect();
      canvas.removeEventListener("wheel", onWheel);
      canvas.removeEventListener("pointerdown", onDown);
      canvas.removeEventListener("pointermove", onMove);
      canvas.removeEventListener("pointerup", onUp);
    };
  }, [draw]);

  // Zoom about the canvas centre (the centre world point is pan-invariant, so only
  // the zoom changes).
  const zoomBy = (factor: number) => {
    view.current.zoom = Math.min(10, Math.max(0.5, view.current.zoom * factor));
    draw();
  };
  const resetView = () => {
    view.current = { zoom: 1, panLon: 0, panLat: 0 };
    draw();
  };

  const midOverlap = 2 * midHalf - coarseStep;
  const denseOverlap = 2 * denseHalf - midStep;
  const ov = (deg: number) => (deg > 1e-9 ? `~${degToDistanceLabel(deg)}` : "none");

  return (
    <div className="col" style={{ gap: 8 }}>
      <div ref={wrapRef} style={{ width: "100%", maxWidth: 540, position: "relative" }}>
        <canvas
          ref={canvasRef}
          style={{
            display: "block",
            borderRadius: "var(--r)",
            touchAction: "none",
          }}
        />
        <div className="row" style={{ position: "absolute", top: 8, right: 8, gap: 4 }}>
          <Button type="button" size="sm" variant="outline" title="Zoom out" onClick={() => zoomBy(1 / 1.3)}>
            -
          </Button>
          <Button type="button" size="sm" variant="outline" title="Zoom in" onClick={() => zoomBy(1.3)}>
            +
          </Button>
          <Button type="button" size="sm" variant="outline" title="Reset view" onClick={resetView}>
            Reset
          </Button>
        </div>
      </div>
      <div className="row" style={{ gap: 14, flexWrap: "wrap", fontSize: 11.5 }}>
        <LegendChip color={CASCADE_COLORS.coarse} label={`Coarse ${degToDistanceLabel(coarseStep)}`} ring />
        <LegendChip color={CASCADE_COLORS.mid} label={`Mid ${degToDistanceLabel(midStep)}`} ring />
        <LegendChip color={CASCADE_COLORS.dense} label={`Dense ${degToDistanceLabel(denseStep)}`} />
        <LegendChip color={CASCADE_COLORS.overlap} label="Overlap (re-probed)" />
      </div>
      <span className="hint">
        Each probe point carries a ring per level that hit it, an outer coarse ring, a mid ring, a
        solid dense dot, so nested rings mean the same ground was probed at several levels. A and B
        are two diagonally-adjacent serviceable coarse cells (solid box = refined around A, dashed =
        around B); one level deeper, A' and B' are the mid cells whose dense boxes (amber) overlap.
        Mid overlap {ov(midOverlap)}, dense overlap {ov(denseOverlap)}. Scroll to zoom, drag to pan.
      </span>
    </div>
  );
}

function LegendChip({ color, label, ring }: { color: string; label: string; ring?: boolean }) {
  return (
    <span className="row" style={{ gap: 6, alignItems: "center" }}>
      <span
        aria-hidden
        style={
          ring
            ? {
                width: 11,
                height: 11,
                borderRadius: "50%",
                border: `1.6px solid ${color}`,
                display: "inline-block",
              }
            : {
                width: 10,
                height: 10,
                borderRadius: 2,
                background: color,
                display: "inline-block",
              }
        }
      />
      <span className="faint">{label}</span>
    </span>
  );
}

// Region scope (merchant sweep): an optional [min_lat, min_lon, max_lat, max_lon]
// bbox in knobs.bbox the engine intersects with the India polygon. All four blank =
// the full India polygon (the default). The order mirrors dispatch's MerchantKnobs.
type BboxForm = [string, string, string, string];

function bboxToForm(raw: unknown): BboxForm {
  if (Array.isArray(raw) && raw.length === 4) {
    return raw.map((n) => String(n)) as BboxForm;
  }
  return ["", "", "", ""];
}

// Parse the four inputs -> a bbox, null (all-India), or throw a friendly message.
function readBbox(f: BboxForm): number[] | null {
  const t = f.map((s) => s.trim());
  if (t.every((s) => s === "")) return null; // all blank -> sweep all of India
  if (t.some((s) => s === "")) {
    throw new Error("Region scope needs all four values (or leave all blank for all-India).");
  }
  const nums = t.map(Number);
  if (nums.some((n) => !Number.isFinite(n))) {
    throw new Error("Region scope values must be numbers.");
  }
  const [minLat, minLon, maxLat, maxLon] = nums;
  if (minLat >= maxLat || minLon >= maxLon) {
    throw new Error("Region scope: min lat/lon must be below max lat/lon.");
  }
  return [minLat, minLon, maxLat, maxLon];
}

const BBOX_LABELS = ["Min lat", "Min lon", "Max lat", "Max lon"] as const;

// Order the per-flow sections render in on the Jobs page.
const FLOW_ORDER: readonly Flow[] = [
  "merchant_sweep",
  "brand_sweep",
  "store_sku_map",
  "cart_inventory",
  "availability_build",
  "availability_rebuild",
  "availability_backfill",
  "shelf_id_resolve",
  "search_rank",
  "category_rank",
  "taxonomy_classify",
  "product_match",
];

// The flows an operator can create from the form: the availability jobs are created and
// queued by the control plane.
const CREATABLE_FLOWS: readonly Flow[] = FLOW_ORDER.filter((f) => !MANAGED_FLOWS.has(f));

// Placeholder names in the shape an operator would actually name a scheduled job: what
// it does, at what cadence. This is the one hint that sets the naming convention for the
// whole page, so it is per-flow rather than one generic example.
const FLOW_NAME_HINT: Record<Flow, string> = {
  cart_inventory: "e.g. Blinkit hourly price and stock",
  brand_sweep: "e.g. Zepto nightly brand sweep",
  merchant_sweep: "e.g. Weekly dark-store discovery",
  store_sku_map: "e.g. Weekly store-SKU carriage map",
  taxonomy_classify: "e.g. Daily taxonomy classification",
  product_match: "e.g. Cross-platform SKU match",
  shelf_id_resolve: "e.g. Nightly Zepto shelf-id top-up",
  search_rank: "e.g. Blinkit daily share of search",
  category_rank: "e.g. Blinkit daily share of category",
  availability_build: "",
  availability_rebuild: "",
  availability_backfill: "",
};

const FLOW_BLURB: Record<Flow, string> = {
  cart_inventory: "Price & availability via the cart API, per store",
  brand_sweep: "Tracked-brand SKU discovery via search -> PDP",
  merchant_sweep: "Dark-store discovery via a serviceability sweep",
  store_sku_map: "StorexSKU carriage (carried vs not) via the cart API",
  taxonomy_classify: "LLM classification of SKUs into the canonical taxonomy",
  product_match: "Group a brand's listings into one SKU across platforms",
  shelf_id_resolve: "Per-store sellable ids the sweeps never stated, via the PDP",
  search_rank: "Where every brand sits on the shelf a tracked keyword returns",
  category_rank: "Where every brand sits on the shelf a tracked category returns",
  availability_build: "Enrich, roll up and build Reach as each cart run ends",
  availability_rebuild: "Rebuild a range of days, one day per run",
  availability_backfill: "Close each settled day and fill the Reach counted runs of the last 30 days miss",
};

// "New job" is a split into a per-flow menu: picking a flow opens the form
// pre-seeded to that flow (flow is fixed after creation, so we choose it up front).
function NewJobButton({ onPick }: { onPick: (flow: Flow) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <Button variant="primary" onClick={() => setOpen((o) => !o)}>
        <IconPlus />
        New job
        <ChevronDown size={12} aria-hidden style={{ marginLeft: 2, opacity: 0.85 }} />
      </Button>
      {open && (
        <>
          <div
            style={{ position: "fixed", inset: 0, zIndex: 10 }}
            onClick={() => setOpen(false)}
          />
          <Panel
            style={{
              position: "absolute",
              right: 0,
              top: "calc(100% + 6px)",
              zIndex: 11,
              minWidth: 280,
              padding: 6,
            }}
          >
            {CREATABLE_FLOWS.map((f) => (
              <Button
                key={f}
                variant="outline"
                size="sm"
                style={{
                  width: "100%",
                  justifyContent: "flex-start",
                  textAlign: "left",
                  height: "auto",
                  padding: "8px 10px",
                }}
                onClick={() => {
                  setOpen(false);
                  onPick(f);
                }}
              >
                <div className="col" style={{ gap: 2, alignItems: "flex-start" }}>
                  <b>{flowLabel(f)}</b>
                  <span className="muted" style={{ fontSize: 12, fontWeight: 400 }}>
                    {FLOW_BLURB[f]}
                  </span>
                </div>
              </Button>
            ))}
          </Panel>
        </>
      )}
    </div>
  );
}

export function Jobs() {
  const qc = useQueryClient();
  const notify = useToast();
  const canWrite = useCanWrite();
  // Editing an existing Job, creating a new one seeded to a flow, or closed.
  const [editing, setEditing] = useState<Job | { newFlow: Flow } | null>(null);

  // Deep-link: `/jobs?new=<flow>` (e.g. from the Taxonomy header "Classify SKUs"
  // button) opens the create modal pre-seeded to that flow, then clears the param.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const seed = searchParams.get("new");
    if (canWrite && seed && CREATABLE_FLOWS.includes(seed as Flow)) {
      setEditing({ newFlow: seed as Flow });
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, [canWrite, searchParams, setSearchParams]);

  const jobsQuery = useListJobsV1JobsGet(undefined, {
    query: { refetchInterval: 8000 },
  });
  const jobs = useMemo(() => (jobsQuery.data ?? []) as Job[], [jobsQuery.data]);
  const jobsByFlow = useMemo(() => {
    const m = new Map<Flow, Job[]>();
    for (const j of jobs) {
      const arr = m.get(j.flow as Flow);
      if (arr) arr.push(j);
      else m.set(j.flow as Flow, [j]);
    }
    return m;
  }, [jobs]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: getListJobsV1JobsGetQueryKey() });
    qc.invalidateQueries({ queryKey: getListRunsV1RunsGetQueryKey() });
  };

  const toggle = useUpdateJobV1JobsJobIdPatch<Error>({
    mutation: {
      onSuccess: (j) => {
        notify(`${j.name} ${j.enabled ? "enabled" : "disabled"}`);
        invalidate();
      },
      onError: (e: Error) => notify(e.message, "crit"),
    },
  });

  const remove = useDeleteJobV1JobsJobIdDelete<Error>({
    mutation: {
      onSuccess: () => {
        notify("Job deleted");
        invalidate();
      },
      onError: (e: Error) => notify(e.message, "crit"),
    },
  });

  const runNow = useRunNowV1JobsJobIdRunsPost<Error>({
    mutation: {
      onSuccess: () => {
        notify("Run started");
        invalidate();
      },
      onError: (e: Error) => notify(e.message, "crit"),
    },
  });

  // -- export / import (cross-environment job-config sync) --------------------
  // Hold the picked File in state (not read back off the input) so the file input
  // can be cleared on every pick -- otherwise re-selecting the same file fires no
  // `change` event and the import silently no-ops.
  const importFileRef = useRef<HTMLInputElement>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importResult, setImportResult] = useState<JobImportResult | null>(null);
  const importJobs = useImportJobsV1JobsImportPost<Error>();

  async function previewImport(file: File) {
    setImportResult(null);
    setImportFile(file);
    try {
      const res = await importJobs.mutateAsync({
        data: { file },
        params: { dry_run: true },
      });
      setImportResult(res);
    } catch (e) {
      notify((e as Error).message, "crit");
    }
  }

  async function applyImport() {
    if (!importFile) return;
    try {
      const res = await importJobs.mutateAsync({
        data: { file: importFile },
        params: { dry_run: false },
      });
      setImportResult(res);
      invalidate();
      notify(`Imported: ${(res.created ?? []).length} created, ${res.updated ?? 0} updated`);
    } catch (e) {
      notify((e as Error).message, "crit");
    }
  }

  const total = jobs.length;
  const enabled = jobs.filter((j) => j.enabled).length;
  const running = jobs.filter((j) => {
    const r = j.last_run;
    return r && (r.status === "running" || r.status === "claimed" || r.status === "queued");
  }).length;
  const failed = jobs.filter((j) => j.last_run?.status === "failed").length;

  return (
    <>
      <PageHeader
        className="mb-3.5"
        title="Jobs"
        description="Scrape jobs across platforms & flows: inputs resolve by query at run time"
        action={
          <Row gap="sm" className="flex-wrap justify-end">
            <Button asChild variant="outline">
              <a href="/v1/jobs/export">Export</a>
            </Button>
            {canWrite && (
              <>
                <Button variant="outline" loading={importJobs.isPending} onClick={() => importFileRef.current?.click()}>
                  Import
                </Button>
                <NewJobButton onPick={(f) => setEditing({ newFlow: f })} />
                <input
                  ref={importFileRef}
                  type="file"
                  accept=".json,application/json"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = ""; // allow re-picking the same file
                    if (f) void previewImport(f);
                  }}
                />
              </>
            )}
          </Row>
        }
      />

      {importResult && (
        <Panel style={{ margin: "0 0 12px", padding: 12 }}>
          <Row justify="between">
            <b>
              Import preview {importResult.applied ? "(applied)" : "(dry run)"}, {" "}
              {(importResult.created ?? []).length} created, {importResult.updated ?? 0} updated,{" "}
              {importResult.unchanged ?? 0} unchanged
              {(importResult.schedules_created ?? 0) > 0 &&
                `, ${importResult.schedules_created} schedule(s) added`}
            </b>
            <Row gap="sm">
              {!importResult.applied && (
                <Button
                  variant="primary"
                  loading={importJobs.isPending}
                  onClick={() => void applyImport()}
                >
                  Apply import
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => {
                  setImportResult(null);
                  setImportFile(null);
                }}
              >
                Dismiss
              </Button>
            </Row>
          </Row>
          {(importResult.warnings ?? []).length > 0 && (
            <ul className="sub" style={{ margin: "8px 0 0" }}>
              {(importResult.warnings ?? []).map((w) => (
                <li key={w}>! {w}</li>
              ))}
            </ul>
          )}
        </Panel>
      )}

      <Grid cols={4} gap="md" className="mb-3.5">
        <Stat
          surface
          label="total jobs"
          value={<AnimatedNumber value={total} />}
          foot={<span className="faint">across all platforms</span>}
        />
        <Stat
          surface
          label="enabled"
          value={<AnimatedNumber value={enabled} />}
          foot={<span className="faint">{`${total - enabled} disabled`}</span>}
        />
        <Stat
          surface
          label={
            <>
              <Dot live />
              running now
            </>
          }
          value={<AnimatedNumber value={running} />}
          tone={running ? "ok" : undefined}
          foot={<span className="faint">active runs</span>}
        />
        <Stat
          surface
          label="failed (last run)"
          value={<AnimatedNumber value={failed} />}
          tone={failed ? "crit" : undefined}
          foot={<span className="faint">needs attention</span>}
        />
      </Grid>

      {jobsQuery.isLoading && !jobsQuery.data ? (
        <Panel>
          <Panel.Body tight>
            <SkeletonRegion label="Loading jobs">
              <Stack gap="sm" className="p-4">
                {Array.from({ length: 4 }, (_, i) => (
                  <Skeleton key={i} h={28} />
                ))}
              </Stack>
            </SkeletonRegion>
          </Panel.Body>
        </Panel>
      ) : jobsQuery.isError && jobs.length === 0 ? (
        <Panel>
          <Panel.Body>
            <ErrorState
              compact
              title="Could not load jobs"
              message="Is the control plane up?"
              onRetry={() => jobsQuery.refetch()}
            />
          </Panel.Body>
        </Panel>
      ) : jobs.length === 0 ? (
        <Panel>
          <Panel.Body>
            <EmptyState compact title="No jobs yet">
              Create one with the New job button.
            </EmptyState>
          </Panel.Body>
        </Panel>
      ) : (
        <Stack gap="lg">
          {jobsQuery.isError && (
            <span className="text-crit text-[12px]">
              Live refresh failed - showing the last loaded jobs.
            </span>
          )}
          {Object.values(FlowCategory).map((category) => (
            <section key={category} aria-labelledby={`jobs-${category}`}>
              <Row justify="between" className="mb-2">
                <h2 id={`jobs-${category}`} className="text-[13px] font-semibold uppercase tracking-wide text-ink-2">
                  {CATEGORY_LABEL[category]}
                </h2>
                {category === "availability" && (
                  <Link to="/availability" className="text-[12px]">Open the Availability pipeline</Link>
                )}
              </Row>
              <Stack gap="md">
                {FLOW_ORDER.filter((f) => FLOW_CATEGORY[f] === category).map((f) => (
                  <FlowSection
                    key={f}
                    flow={f}
                    jobs={jobsByFlow.get(f) ?? []}
                    busy={toggle.isPending || remove.isPending || runNow.isPending}
                    canWrite={canWrite}
                    onToggle={(j) => toggle.mutate({ jobId: j.id, data: { enabled: !j.enabled } })}
                    onRun={(id) => runNow.mutate({ jobId: id })}
                    onEdit={(j) => setEditing(j)}
                    onDelete={(j) => {
                      if (confirm(`Delete job "${j.name}"? This cannot be undone.`)) remove.mutate({ jobId: j.id });
                    }}
                  />
                ))}
              </Stack>
            </section>
          ))}
        </Stack>
      )}

      {editing && (
        <JobEditor
          job={"newFlow" in editing ? null : editing}
          initialFlow={"newFlow" in editing ? editing.newFlow : undefined}
          onClose={() => setEditing(null)}
          onSaved={() => {
            invalidate();
            setEditing(null);
          }}
        />
      )}
    </>
  );
}

function LastRunCell({ run }: { run: JobLastRun | null | undefined }) {
  if (!run) return <span className="faint" style={{ fontSize: 11.5 }}>never run</span>;
  const tone = statusTone(run.status);
  const live = run.status === "running" || run.status === "claimed" || run.status === "queued";
  return (
    <span className="row" style={{ gap: 6 }}>
      <Badge variant={toneVariant(tone)}>
        {live && <Dot live />}
        {run.status}
      </Badge>
      <span className="faint num" style={{ fontSize: 11 }}>
        {ago(run.created_at)}
      </span>
    </span>
  );
}

function JobRow({
  job,
  busy,
  canWrite,
  onToggle,
  onRun,
  onEdit,
  onDelete,
}: {
  job: Job;
  busy: boolean;
  canWrite: boolean;
  onToggle: () => void;
  onRun: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  return (
    <tr style={job.enabled ? undefined : { opacity: 0.78 }}>
      <td
        className="strong"
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        <Link
          to={`/jobs/${job.id}`}
          title={job.name}
          style={{
            color: "var(--ink)",
            fontWeight: 600,
            textDecoration: "none",
          }}
        >
          {job.name}
        </Link>
      </td>
      <td><span className={`plat ${job.platform}`}>{platformLabel(job.platform)}</span></td>
      <td className="c">
        {canWrite ? (
          <Switch
            checked={job.enabled}
            style={{ margin: "auto", display: "block" }}
            disabled={busy}
            onCheckedChange={onToggle}
            aria-label="toggle enabled"
          />
        ) : (
          <Badge variant={job.enabled ? "outline-success" : "outline"}>
            {job.enabled ? "enabled" : "disabled"}
          </Badge>
        )}
      </td>
      <td><LastRunCell run={job.last_run} /></td>
      <td className="r">
        {canWrite && (
          <div className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
            <Button variant="primary" size="sm" disabled={busy || !job.enabled} onClick={onRun}>
              Run now
            </Button>
            {/* The control plane owns an availability job's config; a seeded one is
                disabled rather than deleted, which the API enforces too. */}
            {!MANAGED_FLOWS.has(job.flow) && <Button size="sm" onClick={onEdit}>Edit</Button>}
            {!SEEDED_FLOWS.has(job.flow) && (
              <Button variant="destructive" size="sm" disabled={busy} onClick={onDelete}>Delete</Button>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

// -- per-flow section ---------------------------------------------------------
// One panel per flow so the operator scans cart / brand / merchant jobs apart
// instead of one mixed list. Empty flows still render a panel with a hint so the
// page shape stays stable and a missing flow reads as "nothing configured".
function FlowSection({
  flow,
  jobs,
  busy,
  canWrite,
  onToggle,
  onRun,
  onEdit,
  onDelete,
}: {
  flow: Flow;
  jobs: Job[];
  busy: boolean;
  canWrite: boolean;
  onToggle: (j: Job) => void;
  onRun: (id: string) => void;
  onEdit: (j: Job) => void;
  onDelete: (j: Job) => void;
}) {
  return (
    <Panel>
      <Panel.Head title={flowLabel(flow)}>
        <span className="faint" style={{ fontSize: 11.5, marginLeft: 10 }}>{FLOW_BLURB[flow]}</span>
        <div className="flex-1" />
        <span className="faint num" style={{ fontSize: 11 }}>
          {jobs.length} {jobs.length === 1 ? "job" : "jobs"}
        </span>
      </Panel.Head>
      <Panel.Body tight>
        {jobs.length === 0 ? (
          <div className="muted" style={{ textAlign: "center", padding: 16, fontSize: 12.5 }}>
            No {flowLabel(flow).toLowerCase()} jobs yet.
          </div>
        ) : (
          <table className="tbl" style={{ tableLayout: "fixed", width: "100%" }}>
            {/* shared widths so every flow's table lines up column-for-column */}
            <colgroup>
              <col style={{ width: "auto" }} />
              <col style={{ width: 140 }} />
              <col style={{ width: 90 }} />
              <col style={{ width: 170 }} />
              <col style={{ width: 230 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Name</th>
                <th>Platform</th>
                <th className="c">Enabled</th>
                <th>Last run</th>
                <th className="r">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <JobRow
                  key={j.id}
                  job={j}
                  busy={busy}
                  canWrite={canWrite}
                  onToggle={() => onToggle(j)}
                  onRun={() => onRun(j.id)}
                  onEdit={() => onEdit(j)}
                  onDelete={() => onDelete(j)}
                />
              ))}
            </tbody>
          </table>
        )}
      </Panel.Body>
    </Panel>
  );
}

// -- editor modal -------------------------------------------------------------
function JobEditor({
  job,
  initialFlow,
  onClose,
  onSaved,
}: {
  job: Job | null;
  initialFlow?: Flow;
  onClose: () => void;
  onSaved: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const title =
    job === null ? `New ${initialFlow ? flowLabel(initialFlow).toLowerCase() : ""} job`.replace(/\s+/g, " ").trim() : "Edit job";
  return (
    <ModalShell title={title} onClose={onClose}>
      <JobForm job={job} initialFlow={initialFlow} onSaved={onSaved} onCancel={onClose} />
    </ModalShell>
  );
}

// -- job settings form --------------------------------------------------------
// The editor body, decoupled from the modal shell so the job detail page can host
// it inline. `onCancel` omitted (inline use) drops the Cancel button.
export function JobForm({
  job,
  initialFlow,
  onSaved,
  onCancel,
}: {
  job: Job | null;
  initialFlow?: Flow;
  onSaved: () => void;
  onCancel?: () => void;
}) {
  const notify = useToast();
  const isNew = job === null;

  const [name, setName] = useState(job?.name ?? "");
  const runnablePlatforms = platformKeysFor(
    (job?.flow as Flow) ?? initialFlow ?? Flow.cart_inventory,
  );
  const [platform, setPlatform] = useState<Platform>(
    job?.platform ?? runnablePlatforms[0],
  );
  // Flow is fixed at creation (seeded from the "New job" menu), never edited here.
  const [flow] = useState<Flow>((job?.flow as Flow) ?? initialFlow ?? "cart_inventory");
  // "all" | array of brand names, as stored. A new job starts at "all"; a flow with no
  // brand axis has no key to read and never saves this state.
  const [brandScope, setBrandScope] = useState<BrandScope>(() => job?.inputs.brand_scope ?? "all");
  // "all_active" | array of store ids. Only the rank flows surface a picker for it: every
  // other flow probes the whole active dimension by design. A new job starts at
  // "all_active"; a flow with no store axis never saves this state.
  const [storeScope, setStoreScope] = useState<StoreScope>(
    () => job?.inputs.store_scope ?? "all_active",
  );
  const [forceRefresh, setForceRefresh] = useState(Boolean(job?.knobs?.force_refresh));
  // Cart-inventory: scope the sweep to the store_sku_map carriage matrix (proven-carried
  // store x SKU pairs) instead of the full cartesian. Default ON, only an explicit
  // `use_store_sku_map: false` turns it off (absent = on, matching the backend default).
  const [useStoreSkuMap, setUseStoreSkuMap] = useState(
    job?.inputs?.use_store_sku_map !== false,
  );
  // Cart / carriage: probe combo (multipack) SKUs too. Default OFF, a combo draws from
  // the same shelf as its base single, so probing it double-counts that stock.
  const [includeBundles, setIncludeBundles] = useState(Boolean(job?.knobs?.include_bundles));
  // "dimension" (every active SKU) is the engine default; only the opt-in is stored.
  const [matchedUniverse, setMatchedUniverse] = useState(job?.knobs?.sku_source === "matched");
  const [includeInactive, setIncludeInactive] = useState(job?.knobs?.include_inactive === true);
  // Matching excludes mixed bundles by default, so absent => on and only the opt-out is
  // stored (mirrors use_store_sku_map, the other on-by-default toggle).
  const [excludeCombo, setExcludeCombo] = useState(job?.knobs?.exclude_combo !== false);
  // Reuse remembered verdicts. On by default; only the explicit opt-out is stored.
  const [useCache, setUseCache] = useState(job?.knobs?.use_cache !== false);
  // Shelf-id resolve: harvest ids from search before spending a PDP on each pair. OFF by
  // default -- the prepass can only ADD answers, but it is a second network surface whose
  // block modes all read as an empty shelf, so it is asked for rather than assumed. Only
  // the explicit opt-in is stored.
  const [searchFirst, setSearchFirst] = useState(job?.knobs?.search_first === true);
  const [knobForm, setKnobForm] = useState<Record<string, InputValue>>(() =>
    Object.fromEntries(Object.entries(job?.knobs ?? {}).map(([key, value]) => [key, descriptorValue(value)])),
  );
  const [knobErrors, setKnobErrors] = useState<Record<string, string>>({});
  const [tuningFilter, setTuningFilter] = useState("");
  const [advancedGroups, setAdvancedGroups] = useState<Set<string>>(() => new Set());
  // New merchant sweeps default to the all-India bbox (so the map shows a box to
  // drag/shrink); existing jobs keep whatever was saved (blank = all-India too).
  const [bboxForm, setBboxForm] = useState<BboxForm>(
    job?.knobs?.bbox ? bboxToForm(job.knobs.bbox) : job === null ? [...INDIA_BBOX_FORM] : ["", "", "", ""],
  );
  // Merchant sweep mode (full / update / repair) + its store-closure override.
  // close_stores is derived from mode by default (on for full/update, off for
  // repair); "auto" leaves that to the engine, yes/no force it.
  const [mode, setMode] = useState<string>(String(job?.knobs?.mode ?? "full"));
  // Shelf-id resolve: skip the pairs already answered, or re-fetch all of them. Default ON
  // (absent knob = skip), so only the explicit opt-out is ever stored.
  const [trackedSkusOnly, setTrackedSkusOnly] = useState(job?.knobs?.tracked_skus_only !== false);
  const [recheckAbsent, setRecheckAbsent] = useState(job?.knobs?.recheck_absent === true);
  const [skipExisting, setSkipExisting] = useState<boolean>(job?.knobs?.skip_existing !== false);
  const [closeStores, setCloseStores] = useState<"auto" | "yes" | "no">(
    job?.knobs?.close_stores == null ? "auto" : job.knobs.close_stores ? "yes" : "no",
  );
  // New jobs may optionally define one schedule inline; it's attached right after
  // the job is created. Existing jobs manage schedules via <SchedulesSection>.
  const [attachSchedule, setAttachSchedule] = useState(false);
  const [newCron, setNewCron] = useState("0 9 * * *");
  const [newTimezone, setNewTimezone] = useState("Asia/Kolkata");
  // Per-job run timeout, surfaced in minutes (blank = the server default). A
  // scheduled job is additionally capped to finish before its next fire, so this
  // is the ceiling, not a guarantee.
  const [timeoutMin, setTimeoutMin] = useState<string>(
    job?.wall_clock_cap_seconds != null ? String(Math.round(job.wall_clock_cap_seconds / 60)) : "",
  );
  // Slack notifications for this job's failures. On by default; turn it off for a job
  // that is known-broken so it stops drowning the channel while someone works on it.
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    job?.notifications_enabled !== false,
  );
  const descriptorQuery = useListJobKnobsV1JobsKnobsGet(
    { flow, platform }, { query: { enabled: Boolean(platform) } },
  );
  const descriptors = descriptorQuery.data ?? [];
  const managedDescriptors = descriptors.filter((descriptor) => !SCOPE_KNOBS.has(descriptor.name));
  const descriptorByName = new Map(descriptors.map((descriptor) => [descriptor.name, descriptor]));
  const overridden = (descriptor: JobKnobDescriptor) => knobForm[descriptor.name] != null && knobForm[descriptor.name] !== "";
  const overrideCount = managedDescriptors.filter(overridden).length;
  const searchText = tuningFilter.trim().toLowerCase();
  const setKnob = (key: string, value: InputValue) => {
    setKnobForm((form) => ({ ...form, [key]: value }));
    setKnobErrors((errors) => {
      const next = { ...errors };
      delete next[key];
      return next;
    });
  };
  const knobNum = (key: string): number | null => {
    const override = knobForm[key];
    const value = override === undefined || override === null ? descriptorByName.get(key)?.default : override;
    return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : null;
  };
  const renderKnob = (descriptor: JobKnobDescriptor) => (
    <DescriptorKnobInput
      key={descriptor.name}
      descriptor={descriptor}
      value={knobForm[descriptor.name] ?? null}
      error={knobErrors[descriptor.name] ?? knobValidationError(descriptor, knobForm[descriptor.name] ?? null) ?? undefined}
      onChange={(value) => setKnob(descriptor.name, value)}
    />
  );

  const save = useMutation({
    mutationFn: async () => {
      const inputs = jobInputsPayload({
        flow,
        brandScope,
        storeScope,
        useStoreSkuMap,
        emphasisNodeId: job?.inputs.emphasis_node_id,
      });
      const errors: Record<string, string> = {};
      for (const descriptor of managedDescriptors) {
        if (descriptor.name === "store_sample" && mode !== "sampled") continue;
        if (descriptor.name === "update_margin" && mode !== "update") continue;
        if (descriptor.name === "repair_margin" && mode !== "repair") continue;
        const error = knobValidationError(descriptor, knobForm[descriptor.name] ?? null);
        if (error) errors[descriptor.name] = error;
      }
      setKnobErrors(errors);
      if (Object.keys(errors).length) throw new Error("Fix the highlighted knob values.");
      // A descriptor-managed key never survives a reset, even when hidden by a mode.
      const managed = new Set([
        ...managedDescriptors.map((descriptor) => descriptor.name),
        ...FLOW_SCOPE_KNOBS[flow],
      ]);
      const knobs: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(job?.knobs ?? {})) {
        if (!managed.has(key)) knobs[key] = value;
      }
      for (const descriptor of managedDescriptors) {
        const value = knobForm[descriptor.name];
        if (value === null || value === undefined || value === "") continue;
        if (descriptor.name === "store_sample" && mode !== "sampled") continue;
        if (descriptor.name === "update_margin" && mode !== "update") continue;
        if (descriptor.name === "repair_margin" && mode !== "repair") continue;
        knobs[descriptor.name] = value;
      }
      // Off is the engine default, so only the explicit opt-in is persisted (and turning
      // it back off removes the key, since include_bundles is in the managed set).
      if ((flow === "cart_inventory" || flow === "store_sku_map") && includeBundles) {
        knobs.include_bundles = true;
      }
      if (flow === "cart_inventory" && includeInactive) knobs.include_inactive = true;
      // On is the engine default, so only the explicit opt-out is persisted.
      if (flow === "product_match" && !excludeCombo) knobs.exclude_combo = false;
      if (flow === "product_match" && !useCache) knobs.use_cache = false;
      // Off is the engine default here, so the explicit opt-IN is what gets persisted.
      if (flow === "shelf_id_resolve" && searchFirst) knobs.search_first = true;
      if ((flow === "cart_inventory" || flow === "store_sku_map") && matchedUniverse) {
        knobs.sku_source = "matched";
      }
      if (flow === "merchant_sweep") {
        if (forceRefresh) knobs.force_refresh = true;
        const bbox = readBbox(bboxForm); // throws a friendly message on bad input
        if (bbox) knobs.bbox = bbox;
        // Sweep mode + tuning. "full" and "auto" are the engine defaults -> omit
        // them to keep the stored knobs lean.
        if (mode && mode !== "full") knobs.mode = mode;
        if (closeStores !== "auto") knobs.close_stores = closeStores === "yes";
      }
      if (flow === "brand_sweep") {
        // "full" is the engine default (retires missing SKUs) -> omit it. Sampled
        // reveals the optional store-sample K (blank => per-platform default).
        if (mode && mode !== "full") knobs.mode = mode;
        else delete knobs.mode;
        if (mode !== "sampled") delete knobs.store_sample;
      }
      if (flow === "shelf_id_resolve") {
        // Skipping is the engine default, so only the opt-out is persisted (and ticking it
        // back on removes the key, since skip_existing is in the managed set above).
        if (!skipExisting) knobs.skip_existing = false;
        if (recheckAbsent) knobs.recheck_absent = true;
        if (!trackedSkusOnly) knobs.tracked_skus_only = false;
      }
      // Minutes -> seconds; blank/invalid => null so the server falls back to its default.
      const capRaw = timeoutMin.trim();
      const capNum = capRaw === "" ? Number.NaN : Number(capRaw);
      const wall_clock_cap_seconds =
        Number.isFinite(capNum) && capNum > 0 ? Math.round(capNum * 60) : null;
      if (isNew) {
        const created = await createJobV1JobsPost({
          name: name.trim(),
          platform,
          flow,
          inputs: inputs as Record<string, unknown>,
          knobs,
          wall_clock_cap_seconds,
          enabled: true,
          notifications_enabled: notificationsEnabled,
        });
        // Attach the optional inline schedule to the freshly-created job.
        if (attachSchedule && newCron.trim()) {
          await createScheduleV1JobsJobIdSchedulesPost(created.id, {
            cron: newCron.trim(),
            timezone: newTimezone,
            enabled: true,
          });
        }
        return created;
      }
      return updateJobV1JobsJobIdPatch(job.id, {
        name: name.trim(),
        inputs: inputs as Record<string, unknown>,
        knobs,
        wall_clock_cap_seconds,
        notifications_enabled: notificationsEnabled,
      });
    },
    onSuccess: () => {
      notify(isNew ? "Job created" : "Job updated");
      onSaved();
    },
    onError: (error: Error) => {
      if (error instanceof ApiError && error.status === 422 && Array.isArray(error.detail)) {
        const fieldErrors: Record<string, string> = {};
        for (const issue of error.detail) {
          if (typeof issue === "string") {
            const match = /^knobs\.([^.:]+):\s*(.+)$/.exec(issue);
            if (match && managedDescriptors.some((descriptor) => descriptor.name === match[1])) fieldErrors[match[1]] = match[2];
          } else if (issue && typeof issue === "object" && "loc" in issue && "msg" in issue && Array.isArray(issue.loc)) {
            const knobIndex = issue.loc.indexOf("knobs");
            const name: unknown = issue.loc[knobIndex + 1];
            if (typeof name === "string" && managedDescriptors.some((descriptor) => descriptor.name === name)) fieldErrors[name] = String(issue.msg);
          }
        }
        if (Object.keys(fieldErrors).length) {
          setKnobErrors(fieldErrors);
          return;
        }
      }
      notify(error.message, "crit");
    },
  });

  // ``platform`` is seeded from runnablePlatforms[0], so an empty roster leaves it unset.
  const canSave = name.trim().length > 0 && Boolean(platform) && descriptorQuery.isSuccess && !save.isPending;

  return (
    <>
      <div className="px-3.5 py-3" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="Job name" htmlFor="job-name">
            <Input id="job-name" autoFocus placeholder={FLOW_NAME_HINT[flow]} value={name} onChange={(event) => setName(event.currentTarget.value)} />
          </FormField>
          <FormField label="Flow" htmlFor="job-flow">
            <Input id="job-flow" value={flowLabel(flow)} readOnly className="bg-muted text-muted-foreground" />
          </FormField>
        </div>

        <FormField label="Run timeout (minutes)" htmlFor="job-timeout"
          hint="Maximum run duration. Blank uses the server default; a scheduled job stops before its next run.">
          <Input id="job-timeout" type="number" min={1} placeholder="server default (360)" value={timeoutMin} onChange={(event) => setTimeoutMin(event.currentTarget.value)} />
        </FormField>

        <FormField label="Slack notifications" htmlFor="job-notifications"
          hint="Post failed or degraded runs to Slack. Infra alerts remain on when disabled.">
          <Switch id="job-notifications" checked={notificationsEnabled} onCheckedChange={setNotificationsEnabled} />
        </FormField>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="Platform">
            {flow === "product_match" ? (
              // Matching reads every platform in one run - a match IS a group of listings
              // from different ones - so there is nothing to choose here. The job still
              // carries a platform because the jobs table has the column; it is not a scope,
              // so offering a picker would imply a narrowing that does not happen.
              <span className="hint">
                Runs across every platform. A match is a group of listings from different
                platforms, so this flow has no per-platform scope.
              </span>
            ) : isNew && runnablePlatforms.length === 0 ? (
              <EmptyState compact title="No runnable platforms">
                No platform has the {flowLabel(flow)} capability.
              </EmptyState>
            ) : (
              isNew ? (
                <SegmentedControl
                  label="Platform"
                  value={platform}
                  options={runnablePlatforms.map((candidate) => ({ value: candidate, label: platformLabel(candidate) }))}
                  onChange={(selected) => {
                    if (selected === platform) return;
                    setPlatform(selected);
                    setStoreScope("all_active");
                    setKnobForm({});
                    setKnobErrors({});
                  }}
                />
              ) : <span className="text-sm text-foreground">{platformLabel(platform)}</span>
            )}
            {!isNew && <span className="text-xs text-muted-foreground">Platform is fixed after creation.</span>}
          </FormField>
        </div>

        {flow !== "store_sku_map" && flow !== "taxonomy_classify" && (
          <div className="divider" style={{ margin: 0 }} />
        )}

        {flow === "merchant_sweep" ? (
          <div className="space-y-4">
            <FormField label="Discovery" htmlFor="merchant-force-refresh" hint="Re-probe live to detect opened or closed stores. Otherwise reuse cached serviceability results.">
              <Switch id="merchant-force-refresh" checked={forceRefresh} onCheckedChange={setForceRefresh} />
            </FormField>
            <FormField label="Region scope" hint="Blank coordinates cover all India. Set all four for a smaller sweep.">
              <BboxMapPicker bbox={bboxForm} onChange={setBboxForm} />
              <Grid cols={4} gap="md" style={{ marginTop: 12 }}>
                {BBOX_LABELS.map((lbl, i) => (
                  <div className="field" key={lbl}>
                    <label className="label" style={{ letterSpacing: ".08em" }}>{lbl}</label>
                    <Input
                      className="mono"
                      type="number"
                      step="any"
                      placeholder="-"
                      value={bboxForm[i] ?? ""}
                      onChange={(e) => {
                        // Read the value synchronously: React nulls e.currentTarget
                        // once the handler returns, and this updater may run later.
                        const v = e.currentTarget.value;
                        setBboxForm((b) => {
                          const next = [...b] as BboxForm;
                          next[i] = v;
                          return next;
                        });
                      }}
                    />
                  </div>
                ))}
              </Grid>
              <span className="hint" style={{ marginTop: 4 }}>
                <strong>Blank = all of India</strong> (the default). Set all four to scope the sweep to a
                bounding box, e.g. central Bengaluru <code className="mono">12.93, 77.56, 12.99, 77.63</code>.
                A national sweep is millions of cells: scope it for a fast test.
              </span>
            </FormField>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label="Sweep mode" htmlFor="merchant-mode" hint={MERCHANT_MODES.find((option) => option.value === mode)?.hint}>
                <SharedSelect id="merchant-mode" value={mode} onChange={(event) => setMode(event.currentTarget.value)}>
                  {MERCHANT_MODES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                </SharedSelect>
              </FormField>
              <FormField label="Store closure" htmlFor="merchant-close-stores" hint="Auto follows the sweep mode; override to keep or close absent stores.">
                <SharedSelect id="merchant-close-stores" value={closeStores} onChange={(event) => setCloseStores(event.currentTarget.value as "auto" | "yes" | "no")}>
                  <option value="auto">Auto, on for full/update</option>
                  <option value="yes">Force on</option>
                  <option value="no">Force off</option>
                </SharedSelect>
              </FormField>
            </div>
          </div>
        ) : flow === "brand_sweep" ? (
          <div className="col" style={{ gap: 18 }}>
            <FormField label="Store sampling" htmlFor="brand-mode" hint={BRAND_MODES.find((option) => option.value === mode)?.hint}>
              <SharedSelect id="brand-mode" value={mode} onChange={(event) => setMode(event.currentTarget.value)}>
                {BRAND_MODES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </SharedSelect>
            </FormField>
          </div>
        ) : flow === "store_sku_map" || flow === "taxonomy_classify" ? null : flow ===
          "product_match" ? (
          // Brands are the only scope: matching compares one brand's listings across
          // platforms, so there is no store axis and no store-SKU map to follow.
          <div className="col" style={{ gap: 18 }}>
            <BrandScopeField value={brandScope} onChange={setBrandScope} />
            <div className="field">
              <label className="label">Remembered verdicts</label>
              <label className="row" style={{ gap: 8, alignItems: "center", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={useCache}
                  onChange={(e) => setUseCache(e.currentTarget.checked)}
                />
                <span>Reuse cached judgements, only ask the model about new pairs</span>
              </label>
              <span className="hint">
                <strong>On by default.</strong> Adjudication is the entire cost of this flow,
                and between runs a catalogue barely moves: the same pair gets the same
                question and the same answer. A verdict is remembered against the two
                listings AND their names, so it survives a re-run but is thrown away the
                moment a platform rewords a listing, which is exactly when the answer might
                change. Turn off for a full re-judge, ignoring and refreshing the cache;
                that is the honest way to re-measure after changing the thresholds or the
                prompt.
              </span>
            </div>

            <div className="field">
              <label className="label">Combo SKUs</label>
              <label className="row" style={{ gap: 8, alignItems: "center", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={excludeCombo}
                  onChange={(e) => setExcludeCombo(e.currentTarget.checked)}
                />
                <span>Exclude combos: hold out multipacks and mixed bundles</span>
              </label>
              <span className="hint">
                <strong>On by default.</strong> "Combo" means the same here as on the Brands
                page: a SKU the dimension flags as a bundle, which covers both a{" "}
                <em>multipack</em> ("Pack of 4", which aliases a base product the grid already
                has a row for) and a <em>mixed bundle</em> ("Shampoo + Conditioner", a gift
                hamper). Held out because a bundle is not one sellable product: its size adds
                up to a total that means nothing and its name normalises to whichever product
                is written first. The rule is <code>is_bundle OR pack_count &gt; 1</code>: the
                flag alone is not enough, because platforms set it inconsistently (Flipkart
                flags none), which would drop one platform's multipack and keep another's.
                Turn off to compare at pack level instead of product level.
              </span>
            </div>
          </div>
        ) : flow === "shelf_id_resolve" ? (
          <div className="col" style={{ gap: 18 }}>
            <BrandScopeField value={brandScope} onChange={setBrandScope} />
            <div className="field">
              <label className="label">SKU selection</label>
              <label className="row" style={{ gap: 8, alignItems: "center", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={trackedSkusOnly}
                  onChange={(e) => setTrackedSkusOnly(e.currentTarget.checked)}
                />
                <span>Tracked SKUs only</span>
              </label>
              <span className="hint">
                On: use tracked matched products, as in cart inventory and store mapping with
                matched-product selection. Brands without matched products use their existing
                SKU tracking settings. An explicitly selected brand overrides its brand-level
                disable. Off: include all active SKUs in the
                selected brand scope, including disabled SKUs and brands. Both options exclude
                bundles and use the selected eligible stores. Skip existing answers applies
                independently below.
              </span>
            </div>
            <div className="field">
              <label className="label">Existing pairs</label>
              <label className="row" style={{ gap: 8, alignItems: "center", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={skipExisting}
                  onChange={(e) => setSkipExisting(e.currentTarget.checked)}
                />
                <span>Skip pairs we already have an answer for</span>
              </label>
              <span className="hint">
                <strong>On by default.</strong> The run asks the platform about every active
                store x SKU pair in scope, one PDP each. With this on it drops the pairs the
                table already has a word on: by default including the ones the platform answered{" "}
                <em>no shelf row</em> for, which are the majority, so each run picks up what
                is still unanswered instead of re-buying the catalogue. Turn it off for a{" "}
                <strong>full re-fetch</strong>: every pair is asked again and the answer is
                compared against the id we hold, which is the only way to catch an id that
                CHANGED. That matters because a stale id reads downstream as a delisting,
                the cart addresses a row the platform no longer honours and sees nothing back.
                Costs one PDP per pair in scope, so it is a deliberate run, not a default.
              </span>
            </div>
            <div className="field">
              <label className="row" style={{ gap: 8, alignItems: "center", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={recheckAbsent}
                  disabled={!skipExisting}
                  onChange={(e) => setRecheckAbsent(e.currentTarget.checked)}
                />
                <span>Recheck confirmed absent pairs</span>
              </label>
              <span className="hint">
                {skipExisting
                  ? "Also scrape pairs previously confirmed to have no shelf ID. Pairs with a shelf ID are still skipped."
                  : "All pairs in scope will be scraped, including confirmed absences."}
              </span>
            </div>
            <div className="field">
              <label className="label">Search prepass</label>
              <label className="row" style={{ gap: 8, alignItems: "center", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={searchFirst}
                  onChange={(e) => setSearchFirst(e.currentTarget.checked)}
                />
                <span>Harvest ids from search before spending a PDP on each pair</span>
              </label>
              <span className="hint">
                <strong>Off by default.</strong> On platforms whose search cards already state
                the store-local id, one search answers what one PDP answers times the products
                on the page: measured on Flipkart at ~1.7 pairs saved per search request, on
                a request type that barely burns proxies (2 leases and no failures for two
                whole stores, against ~4.6 answers per lease for the PDP pass). It runs before
                the worklist is read, so what it answers is simply not asked again. It can only{" "}
                <strong>add</strong>: search states presence and never absence, so a store it
                never reached or a product no card returned stays in the PDP worklist exactly
                as before. Off by default because a refused search still reads here as an empty
                shelf: Flipkart answers 200 with an in-body redirect and no results, which
                costs the pass its leases and reports a clean success, so turn it on for a run
                once its harvest is worth checking.
              </span>
            </div>
          </div>
        ) : flow === "search_rank" || flow === "category_rank" ? (
          <div className="col" style={{ gap: 18 }}>
            <div className="field">
              <label className="label">Shelf scope</label>
              <span className="hint">
                The universe is the <Link to="/rank">Rank registry</Link>, not a setting on
                this job: every enabled {flow === "search_rank" ? "keyword" : "category"} is
                measured. A job that carried its own copy would drift from the registry the
                first time somebody added a term, and the whole point of the enabled switch
                is that turning it off stops the measurement everywhere at once.
              </span>
            </div>
            <StoreScopeField platform={platform} value={storeScope} onChange={setStoreScope} />
            <div className="field">
              <label className="label">What a run writes</label>
              <span className="hint">
                One row per visible slot on page one, plus one row per question asked
                whatever it answered. The second is what makes a share a fraction: a
                keyword that came back empty and a keyword whose chain died both write no
                slots, and only the probe row can tell them apart. The run reports its{" "}
                <strong>coverage</strong> separately from its status for the same reason,
                a run that measured half its shelves still finishes green.
              </span>
            </div>
          </div>
        ) : (
          <div className="col" style={{ gap: 18 }}>
            <BrandScopeField value={brandScope} onChange={setBrandScope} />
            <div className="field">
              <label className="label">Coverage scope</label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={useStoreSkuMap}
                  disabled={flow === "cart_inventory" && includeInactive}
                  onChange={(e) => setUseStoreSkuMap(e.currentTarget.checked)}
                />
                <span>Use store-SKU map - probe only proven-carried (store, SKU) pairs</span>
              </label>
              <span className="hint">
                {flow === "cart_inventory" && includeInactive ? (
                  <>
                    Inactive coverage overrides this setting. Every included SKU is checked at
                    every active store; your saved map setting resumes when inactive coverage is off.
                  </>
                ) : (
                  <>
                    <strong>On by default.</strong> Sweeps only the pairs a prior{" "}
                    <strong>store-SKU map</strong> run proved carried, skipping the full store x SKU
                    cartesian (far fewer requests, no false-delisting). Coverage follows the last map
                    sweep, so a store or SKU only appears once the map includes it; if no map exists
                    yet, it falls back to the full cartesian for that run. Turn off to always probe
                    every active store against every active SKU.
                  </>
                )}
              </span>
            </div>
          </div>
        )}

        {(flow === "cart_inventory" || flow === "store_sku_map") && (
          <>
            <div className="divider" style={{ margin: 0 }} />
            <div className="field">
              <label className="label">SKU universe</label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={matchedUniverse}
                  disabled={flow === "cart_inventory" && includeInactive}
                  onChange={(e) => setMatchedUniverse(e.currentTarget.checked)}
                />
                <span>Only tracked matched products - drive this run from Product match</span>
              </label>
              <span className="hint">
                {flow === "cart_inventory" && includeInactive ? (
                  <>
                    Inactive coverage overrides matched selection and uses the full SKU dimension
                    within this job's brand scope. Your saved setting resumes when inactive coverage is off.
                  </>
                ) : (
                  <>
                    <strong>Off by default.</strong> On, the run probes only listings belonging to
                    a matched product that is <em>tracked</em>, so untracking a SKU on the brand
                    page removes it from every platform at once. Details still come from the
                    platform's own <code>_skus</code> dimension; the matched catalogue only says
                    which products count. A brand with no matched products falls back to the full
                    dimension and logs loudly - a brand added yesterday must not silently probe
                    nothing while the run reports success.
                  </>
                )}
              </span>
            </div>
            {flow === "cart_inventory" && (
              <div className="field">
                <label className="label">Inactive coverage</label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={includeInactive}
                    onChange={(e) => setIncludeInactive(e.currentTarget.checked)}
                  />
                  <span>Include inactive brands and SKUs</span>
                </label>
                <span className="hint">
                  Includes inactive listings and operator-disabled brands/SKUs within this job's
                  brand scope. Checks every included SKU at every active store, ignoring matched
                  selection and the store-SKU map. Bundle exclusions and SKU/store limits still
                  apply. This can greatly increase requests and runtime.
                </span>
              </div>
            )}
            <div className="field">
              <label className="label">Combo SKUs</label>
              <label className="row" style={{ gap: 8, alignItems: "center", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={includeBundles}
                  onChange={(e) => setIncludeBundles(e.currentTarget.checked)}
                />
                <span>Include combo SKUs: probe multipacks/combos too</span>
              </label>
              <span className="hint">
                <strong>Off by default:</strong> a multipack draws from the same darkstore shelf as
                its base single, so probing it double-counts that stock. Combos are also registered
                untracked, so turning this on only reaches a combo an operator explicitly re-tracked.
              </span>
            </div>
          </>
        )}

        <div className="divider" style={{ margin: 0 }} />

        <FormSection title="Tuning" summary={descriptorQuery.isSuccess ? `${managedDescriptors.length} knobs, ${overrideCount} overridden` : "Loading knobs"}>
          {descriptorQuery.isPending ? <Skeleton style={{ height: 80 }} /> : descriptorQuery.isError ? (
            <ErrorState title="Could not load job knobs" message={String(descriptorQuery.error)} onRetry={() => void descriptorQuery.refetch()} />
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Input aria-label="Filter knobs" className="h-8 max-w-xs text-xs" placeholder="Filter knobs" value={tuningFilter} onChange={(event) => setTuningFilter(event.currentTarget.value)} />
                <Button type="button" variant="ghost" size="sm" disabled={overrideCount === 0}
                  onClick={() => {
                    setKnobForm((form) => Object.fromEntries(Object.entries(form).filter(([key]) => SCOPE_KNOBS.has(key))));
                    setKnobErrors({});
                  }}>Reset all</Button>
              </div>
              {flow === "merchant_sweep" && CASCADE_KEYS.every((key) => knobNum(key) !== null) && (
                <div className="rounded-md border border-border p-3">
                  <p className="mb-2 text-xs font-medium text-foreground">Geo-cascade preview</p>
                  <CascadePreview
                    coarseStep={knobNum("coarse_step")!}
                    midStep={knobNum("mid_step")!}
                    denseStep={knobNum("dense_step")!}
                    midHalf={knobNum("mid_half_width")!}
                    denseHalf={knobNum("dense_half_width")!}
                  />
                </div>
              )}
              {KNOB_GROUPS.map((group) => {
                const fields = managedDescriptors.filter((descriptor) => descriptor.group === group
                  && (descriptor.name !== "store_sample" || mode === "sampled")
                  && (descriptor.name !== "update_margin" || mode === "update")
                  && (descriptor.name !== "repair_margin" || mode === "repair"));
                const matches = fields.filter((descriptor) => !searchText || `${descriptor.name} ${descriptor.label}`.toLowerCase().includes(searchText));
                const changed = fields.filter(overridden).length;
                const advanced = advancedGroups.has(group);
                const visible = matches.filter((descriptor) => descriptor.tier === KnobTier.common || advanced || overridden(descriptor) || Boolean(searchText));
                return matches.length ? (
                  <FormSection key={group} title={knobGroupLabel(group)} summary={`${changed} overridden`} defaultOpen={changed > 0 || Boolean(searchText)}>
                    <div className="space-y-3">
                      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">{visible.map(renderKnob)}</div>
                      {matches.some((descriptor) => descriptor.tier === KnobTier.advanced && !overridden(descriptor)) && !searchText && (
                        <Button type="button" variant="ghost" size="sm" onClick={() => setAdvancedGroups((current) => {
                          const next = new Set(current);
                          if (next.has(group)) next.delete(group); else next.add(group);
                          return next;
                        })}>{advanced ? "Hide advanced" : "Show advanced"}</Button>
                      )}
                    </div>
                  </FormSection>
                ) : null;
              })}
              <p className="text-xs text-muted-foreground">Empty inherits the default. Reset removes an override.</p>
            </div>
          )}
        </FormSection>

        {!isNew && (
          <>
            <div className="divider" style={{ margin: 0 }} />
            <SchedulesSection jobId={job.id} />
          </>
        )}
        {isNew && (
          <>
            <div className="divider" style={{ margin: 0 }} />
            <div className="field">
              <label className="label">Schedule</label>
              <label
                className="row"
                style={{
                  gap: 8,
                  alignItems: "center",
                  cursor: "pointer",
                  marginBottom: 4,
                }}
              >
                <input type="checkbox" checked={attachSchedule} onChange={(e) => setAttachSchedule(e.currentTarget.checked)} />
                <span>Run this job on a schedule</span>
              </label>
              {attachSchedule ? (
                <>
                  <ScheduleBuilder onChange={setNewCron} />
                  <div
                    className="row"
                    style={{
                      gap: 10,
                      flexWrap: "wrap",
                      alignItems: "flex-end",
                      marginTop: 10,
                    }}
                  >
                    <div className="field" style={{ flex: 1, minWidth: 150 }}>
                      <label className="label">Timezone</label>
                      <Select
                        value={newTimezone}
                        onChange={(e) => setNewTimezone(e.currentTarget.value)}
                      >
                        <option>Asia/Kolkata</option>
                        <option>UTC</option>
                      </Select>
                    </div>
                  </div>
                  <span className="hint" style={{ marginTop: 6 }}>
                    The schedule is attached once the job is created. Add more later from the editor.
                  </span>
                </>
              ) : (
                <span className="hint">
                  Leave unchecked to run the job manually. You can attach schedules anytime by editing the job.
                </span>
              )}
            </div>
          </>
        )}
      </div>

      <div
        className="flex items-center gap-2.5 border-b border-border px-3.5 py-[9px]"
        style={{
          borderTop: "1px solid var(--line)",
          borderBottom: 0,
          justifyContent: "flex-end",
        }}
      >
        {onCancel && (
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button variant="primary" disabled={!canSave} onClick={() => save.mutate()}>
          {save.isPending ? "Saving..." : isNew ? "Create job" : "Save changes"}
        </Button>
      </div>
    </>
  );
}

// -- inputs payload -------------------------------------------------------------
// What the form saves as ``jobs.inputs``. The API parses it with the flow's strict model,
// which requires each scope key the flow declares and refuses any other, so each flow sends
// exactly its own keys, each one its "all" literal or a non-empty list of names.
export function jobInputsPayload({
  flow,
  brandScope,
  storeScope,
  useStoreSkuMap,
  emphasisNodeId,
}: {
  flow: Flow;
  brandScope: BrandScope;
  storeScope: StoreScope;
  useStoreSkuMap: boolean;
  // The stored taxonomy emphasis node, absent on a new job. The form has no field for
  // it, so an edit sends back what the job holds.
  emphasisNodeId?: string | null;
}): JobInputs {
  switch (flow) {
    case "availability_build":
    case "availability_rebuild":
    case "availability_backfill":
      // Created and queued by the control plane; the form never opens for one.
      throw new Error(`${flow} jobs are managed by the control plane`);
    case "merchant_sweep":
      // Sweeps a geographic bbox, held in its knobs; it has no brand or store scope.
      return {};
    case "taxonomy_classify":
      // The form has no brand picker for this flow: a new job classifies every brand, and
      // an edit keeps the brand scope and emphasis node the taxonomy page's launch stored.
      return {
        brand_scope: pickedNames(brandScope, "brand"),
        ...(emphasisNodeId === undefined ? {} : { emphasis_node_id: emphasisNodeId }),
      };
    case "product_match":
      // Matching is brand-partitioned and has no store axis.
      return { brand_scope: pickedNames(brandScope, "brand") };
    case "search_rank":
    case "category_rank":
      // The store axis is a job decision: a shelf is measured per store, so a daily job
      // over three metros and a weekly one over the whole dimension are two different
      // jobs. The shelves are the whole enabled registry, read at run time.
      return {
        store_scope: pickedNames(storeScope, "store"),
        ...(flow === "search_rank" ? { keyword_scope: "all" } : { category_scope: "all" }),
      };
    case "brand_sweep":
      // Every active brand, keyword and store (no scope picker); the "Brand limit" knob
      // caps the brand count for testing.
      return { brand_scope: "all", store_scope: "all_active", candidate_scope: "all" };
    case "store_sku_map":
      // Carriage always covers every enabled brand, so it has no brand scope.
      return { store_scope: "all_active" };
    case "cart_inventory":
      return {
        brand_scope: pickedNames(brandScope, "brand"),
        store_scope: "all_active",
        // Default is ON (absent = on), so only persist the explicit opt-out.
        ...(useStoreSkuMap ? {} : { use_store_sku_map: false }),
      };
    case "shelf_id_resolve":
      return { brand_scope: pickedNames(brandScope, "brand"), store_scope: "all_active" };
  }
}

// A subset with nothing picked names nothing: refuse it rather than save a scope that
// widens to everything or measures nothing.
function pickedNames<All extends string>(scope: All | string[], noun: string): All | string[] {
  if (Array.isArray(scope) && scope.length === 0) {
    throw new Error(`Pick at least one ${noun}, or tick the "All" box.`);
  }
  return scope;
}

// -- store scope picker -------------------------------------------------------
// The rank flows are the only ones that surface this: their cost is shelves x stores, so
// which dark stores a run probes is a real operator decision rather than a constant. The
// value is the engine contract: the "all_active" literal, or a non-empty array of store ids.
type StoreScope = "all_active" | string[];

function StoreScopeField({
  platform,
  value,
  onChange,
}: {
  platform: Platform;
  value: StoreScope;
  onChange: (v: StoreScope) => void;
}) {
  // The active dimension, which is the same set the run resolves. A store the operator
  // has since excluded is dropped by the dimension read at run time whether or not it is
  // named here, so naming one cannot smuggle it back into a run.
  const storesQuery = useStoresV1StoresGet({ platform, status: "active", limit: 3000 });
  const stores = useMemo(() => storesQuery.data ?? [], [storesQuery.data]);

  // Mode is local state, not derived from the value: in subset mode the value is briefly
  // an empty array, and deriving would flip the toggle back to "all" mid-selection.
  const [custom, setCustom] = useState(() => Array.isArray(value));
  // The one direction that IS derived. Changing the job's platform resets the scope to
  // the sentinel, and a mode left in subset then renders an unticked "All active stores"
  // box and "0 stores selected" over a value that says every store. Entering subset mode
  // emits `[]` in the same handler, so this never fights a selection in progress: an
  // empty array is still an array.
  useEffect(() => {
    if (!Array.isArray(value)) setCustom(false);
  }, [value]);
  const isAll = !custom;
  const selected = useMemo(
    () => (custom && Array.isArray(value) ? new Set(value) : new Set<string>()),
    [custom, value],
  );

  // A row carries the whole dimension row, because the picker renders one column per
  // attribute rather than one packed string: several platforms write a numeric locality,
  // so "013 . 37634" left an operator guessing which half was the store id. The VALUE is
  // still the store id, which is what the engine filters on. `text` is what the filter
  // box matches, so typing a state or a pincode finds a store no column of the old label
  // showed.
  // Ids already selected but missing from the dimension are kept as id-only rows, so
  // reopening a job that names a since-closed store does not silently drop it.
  const options = useMemo(() => {
    const byId = new Map<string, { id: string; store?: Store; text: string }>();
    for (const st of stores) {
      const text = [st.store_id, st.locality, st.city, st.state, st.pincode]
        .filter(Boolean)
        .join(" ");
      byId.set(st.store_id, { id: st.store_id, store: st, text: text.toLowerCase() });
    }
    for (const id of selected) {
      if (!byId.has(id)) byId.set(id, { id, text: id.toLowerCase() });
    }
    return Array.from(byId.values()).sort((a, b) => {
      const place = (o: { id: string; store?: Store }) =>
        [o.store?.locality ?? "", o.store?.city ?? "", o.id].join(" ");
      return place(a).localeCompare(place(b));
    });
  }, [stores, selected]);

  const [open, setOpen] = useState(false);
  // Ties the trigger to the panel it opens, which is what makes aria-expanded mean anything.
  const pickerId = useId();
  const [filter, setFilter] = useState("");
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  const emit = (ids: Iterable<string>) => onChange(Array.from(ids));
  const toggleId = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    emit(next);
  };

  const setAll = (allOn: boolean) => {
    setCustom(!allOn);
    if (allOn) {
      onChange("all_active");
      setOpen(false);
    } else {
      onChange([]);
      setOpen(true);
    }
  };

  const needle = filter.trim().toLowerCase();
  const visible = options.filter((o) => o.text.includes(needle));

  return (
    <div className="field">
      <label className="label">Store scope</label>
      <Row gap="sm" style={{ marginBottom: 8 }}>
        <input
          type="checkbox"
          checked={isAll}
          onChange={(e) => setAll(e.currentTarget.checked)}
          style={{ cursor: "pointer" }}
        />
        <span>All active stores</span>
      </Row>

      <div ref={boxRef} style={{ position: "relative", opacity: isAll ? 0.5 : 1 }}>
        {/* A combobox rather than a button, because it CONTAINS a remove control per
            selected store. A button may not hold another interactive element: nested, the
            inner one is unreachable by Tab and the two roles conflict. So the trigger takes
            on the keyboard duties the button element was giving it for free. */}
        <div
          role="combobox"
          aria-expanded={open}
          aria-controls={pickerId}
          aria-disabled={isAll}
          tabIndex={isAll ? -1 : 0}
          className="input"
          onClick={() => !isAll && setOpen((o) => !o)}
          onKeyDown={(e) => {
            if (isAll || (e.key !== "Enter" && e.key !== " ")) return;
            e.preventDefault(); // Space would scroll the modal out from under the picker.
            setOpen((o) => !o);
          }}
          style={{
            cursor: isAll ? "default" : "pointer",
            textAlign: "left",
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexWrap: "wrap",
            minHeight: 38,
          }}
        >
          {selected.size === 0 ? (
            <span className="faint">Select stores...</span>
          ) : (
            Array.from(selected).map((id) => (
              <Tag key={id} style={{ fontSize: 11, gap: 4 }}>
                {id}
                <button
                  type="button"
                  aria-label={`Remove ${id}`}
                  title={`Remove ${id}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    const next = new Set(selected);
                    next.delete(id);
                    emit(next);
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                  style={{
                    all: "unset",
                    cursor: "pointer",
                    opacity: 0.6,
                    fontSize: 13,
                    lineHeight: 1,
                  }}
                >
                  <X size={12} aria-hidden />
                </button>
              </Tag>
            ))
          )}
          <span className="grow" />
          <ChevronDown size={11} className="faint" aria-hidden />
        </div>

        {open && !isAll && (
          <Panel
            id={pickerId}
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              right: 0,
              zIndex: 10,
              // Wide enough for the store columns, never wider than the modal it opens
              // inside. A bare min-width overhangs the right edge on a narrow viewport,
              // where the modal is the width of the screen less its padding, and the
              // columns it was widening for cannot be read at all.
              minWidth: "min(680px, 100%)",
              width: "max-content",
              maxWidth: "100%",
              maxHeight: 320,
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 8px 24px rgba(0,0,0,.18)",
            }}
          >
            <div style={{ padding: 8, borderBottom: "1px solid var(--line)" }}>
              <Input
                autoFocus
                placeholder="Filter by locality, city or id..."
                value={filter}
                onChange={(e) => setFilter(e.currentTarget.value)}
              />
            </div>
            <div style={{ overflow: "auto" }}>
              {storesQuery.isLoading && (
                <div className="faint" style={{ padding: 10, fontSize: 12 }}>Loading stores...</div>
              )}
              {/* A read that failed is not an empty dimension, and the two are otherwise
                  indistinguishable here: `stores` stays `[]` either way, so a transient
                  5xx would print "run a merchant sweep first" - a confident, actionable
                  instruction about the platform for a request that simply did not come
                  back - and label the job's own stores "not in the dimension". */}
              {storesQuery.isError && (
                <div className="text-crit text-[12px]" style={{ padding: 10 }}>
                  Could not read the store dimension. Any store listed below is one this
                  job already names.
                </div>
              )}
              {!storesQuery.isLoading && !storesQuery.isError && visible.length === 0 && (
                <div className="faint" style={{ padding: 10, fontSize: 12 }}>
                  {options.length === 0
                    ? "No active stores on this platform yet: run a merchant sweep first."
                    : "No stores match."}
                </div>
              )}
              {visible.length > 0 && (
                <table className="tbl tight sticky-head">
                  <thead>
                    <tr>
                      <th style={{ width: 34 }} aria-label="Selected" />
                      <th>Store id</th>
                      <th>Locality</th>
                      <th>City</th>
                      <th>State</th>
                      <th>Pincode</th>
                      <th>Tier</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((o) => (
                      <tr
                        key={o.id}
                        onClick={() => toggleId(o.id)}
                        style={{ cursor: "pointer" }}
                      >
                        <td className="c">
                          <input
                            type="checkbox"
                            checked={selected.has(o.id)}
                            onChange={() => toggleId(o.id)}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Select ${o.id}`}
                          />
                        </td>
                        <td className="mono strong">{o.id}</td>
                        {o.store ? (
                          <>
                            <td>{o.store.locality || <span className="faint">-</span>}</td>
                            <td>{o.store.city || <span className="faint">-</span>}</td>
                            <td>{o.store.state || <span className="faint">-</span>}</td>
                            <td className="mono">
                              {o.store.pincode || <span className="faint">-</span>}
                            </td>
                            <td>
                              {platformHasLanes(platform) ? (
                                <TierPills tiers={o.store.tiers} fallback={o.store.tier} />
                              ) : (
                                <RolePill tier={o.store.tier} />
                              )}
                            </td>
                          </>
                        ) : (
                          <td colSpan={5} className="faint">
                            {storesQuery.isError
                              ? "dimension unavailable"
                              : "not in the dimension"}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Panel>
        )}
      </div>

      <span className="hint" style={{ marginTop: 6 }}>
        {isAll
          ? "Every active, non-excluded store on this platform is probed for every enabled shelf. A run's request count is shelves x stores, so this is the axis that decides what a run costs."
          : `${selected.size} store${selected.size === 1 ? "" : "s"} selected: pick at least one, or tick the box to probe every active store.`}
      </span>
    </div>
  );
}

// -- brand scope picker -------------------------------------------------------
// Operator picks "all tracked brands" (a toggle) or an explicit subset chosen
// from the registered brands via a multi-select dropdown, no free-text typing,
// so a scope can't reference a brand that isn't tracked. The value is the engine
// contract: the "all" literal, or a non-empty array of brand names.
type BrandScope = "all" | string[];

function BrandScopeField({
  value,
  onChange,
}: {
  value: BrandScope;
  onChange: (v: BrandScope) => void;
}) {
  const brandsQuery = useListBrandsV1BrandsGet();
  // memoised: the `?? []` fallback is a new array each render, which defeats the memos below
  const brands = useMemo(() => brandsQuery.data ?? [], [brandsQuery.data]);

  // "All" vs "explicit subset" is held as local mode state, NOT derived from the
  // value: otherwise unchecking the toggle (which leaves the value at "all" while
  // nothing is picked yet) would immediately re-check itself. Initialised from the
  // incoming value so editing an existing scoped job opens in subset mode.
  const [custom, setCustom] = useState(() => Array.isArray(value));
  const isAll = !custom;
  const selected = useMemo(
    () => (custom && Array.isArray(value) ? new Set(value) : new Set<string>()),
    [custom, value],
  );

  // Options = registered brands, plus any already-selected names no longer
  // tracked (so editing an old job doesn't silently drop them).
  const options = useMemo(() => {
    const names = new Set(brands.map((b) => b.brand));
    for (const s of selected) names.add(s);
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [brands, selected]);

  const [open, setOpen] = useState(false);
  const pickerId = useId();
  const [filter, setFilter] = useState("");
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, [open]);

  // In subset mode the selection emits an array (empty while nothing is picked yet, and
  // the parent refuses to save it empty); the "all" literal is only set by the toggle
  // below.
  const emit = (names: Iterable<string>) => {
    onChange(Array.from(names));
  };
  const toggleName = (name: string) => {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    emit(next);
  };

  const setAll = (allOn: boolean) => {
    setCustom(!allOn);
    if (allOn) {
      onChange("all");
      setOpen(false);
    } else {
      // entering subset mode: clear the "all" sentinel and open the picker.
      onChange([]);
      setOpen(true);
    }
  };

  const visible = options.filter((o) => o.toLowerCase().includes(filter.trim().toLowerCase()));

  return (
    <div className="field">
      <label className="label">Brand scope</label>
      <Row gap="sm" style={{ marginBottom: 8 }}>
        <input
          type="checkbox"
          checked={isAll}
          onChange={(e) => setAll(e.currentTarget.checked)}
          style={{ cursor: "pointer" }}
        />
        <span>All tracked brands</span>
      </Row>

      <div ref={boxRef} style={{ position: "relative", opacity: isAll ? 0.5 : 1 }}>
        {/* A combobox, for the reason the store picker above is one: a button may not hold
            the per-tag remove control, which nested is unreachable by Tab. */}
        <div
          role="combobox"
          aria-expanded={open}
          aria-controls={pickerId}
          aria-disabled={isAll}
          tabIndex={isAll ? -1 : 0}
          className="input"
          onClick={() => !isAll && setOpen((o) => !o)}
          onKeyDown={(e) => {
            if (isAll || (e.key !== "Enter" && e.key !== " ")) return;
            e.preventDefault();
            setOpen((o) => !o);
          }}
          style={{
            cursor: isAll ? "default" : "pointer",
            textAlign: "left",
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexWrap: "wrap",
            minHeight: 38,
          }}
        >
          {selected.size === 0 ? (
            <span className="faint">Select brands...</span>
          ) : (
            Array.from(selected).map((s) => (
              <Tag key={s} style={{ fontSize: 11, gap: 4 }}>
                {s}
                <button
                  type="button"
                  aria-label={`Remove ${s}`}
                  title={`Remove ${s}`}
                  onClick={(e) => {
                    e.stopPropagation(); // don't toggle the dropdown open
                    const next = new Set(selected);
                    next.delete(s);
                    emit(next);
                  }}
                  onKeyDown={(e) => e.stopPropagation()}
                  style={{
                    all: "unset",
                    cursor: "pointer",
                    opacity: 0.6,
                    fontSize: 13,
                    lineHeight: 1,
                  }}
                >
                  <X size={12} aria-hidden />
                </button>
              </Tag>
            ))
          )}
          <span className="flex-1" />
          <ChevronDown size={11} className="faint" aria-hidden />
        </div>

        {open && !isAll && (
          <Panel
            id={pickerId}
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              right: 0,
              zIndex: 10,
              // Never wider than the modal it opens inside; see the store picker above.
              minWidth: "min(680px, 100%)",
              width: "max-content",
              maxWidth: "100%",
              maxHeight: 320,
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 8px 24px rgba(0,0,0,.18)",
            }}
          >
            <div style={{ padding: 8, borderBottom: "1px solid var(--line)" }}>
              <Input
                autoFocus
                placeholder="Filter brands..."
                value={filter}
                onChange={(e) => setFilter(e.currentTarget.value)}
              />
            </div>
            <div style={{ overflowY: "auto", padding: 4 }}>
              {brandsQuery.isLoading && (
                <div className="faint" style={{ padding: 10, fontSize: 12 }}>Loading brands...</div>
              )}
              {!brandsQuery.isLoading && visible.length === 0 && (
                <div className="faint" style={{ padding: 10, fontSize: 12 }}>
                  {options.length === 0 ? "No tracked brands yet." : "No brands match."}
                </div>
              )}
              {visible.map((name) => (
                <label
                  key={name}
                  className="row"
                  style={{
                    gap: 8,
                    alignItems: "center",
                    cursor: "pointer",
                    padding: "6px 8px",
                    borderRadius: "var(--r)",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(name)}
                    onChange={() => toggleName(name)}
                  />
                  <span style={{ fontSize: 13 }}>{name}</span>
                </label>
              ))}
            </div>
          </Panel>
        )}
      </div>

      <span className="hint" style={{ marginTop: 6 }}>
        {isAll
          ? "Every tracked brand is swept on each platform."
          : `${selected.size} brand${selected.size === 1 ? "" : "s"} selected: toggle "All tracked brands" to sweep everything.`}
      </span>
    </div>
  );
}

// -- cron <-> plain-English helpers -------------------------------------------
// The backend stores a standard 5-field cron string (min hour dom month dow). We
// never make a non-tech operator type that: the builder below emits cron, and we
// render it back as a sentence wherever a schedule is shown.
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

const pad2 = (n: number) => String(n).padStart(2, "0");

// Turn a cron string into "Every day at 09:00" etc. Falls back to the raw cron
// for anything outside the four shapes the builder produces.
export function humanizeCron(cron: string): string {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return cron;
  const [min, hour, dom, mon, dow] = parts;
  const m = Number(min);
  const h = Number(hour);
  const validMin = /^\d+$/.test(min) && m >= 0 && m <= 59;
  const validHour = /^\d+$/.test(hour) && h >= 0 && h <= 23;
  const at = `${pad2(h)}:${pad2(m)}`;

  if (mon === "*") {
    // hourly: m * * * *
    if (hour === "*" && dom === "*" && dow === "*" && validMin)
      return m === 0 ? "Every hour, on the hour" : `Every hour at :${pad2(m)}`;
    // daily: m h * * *
    if (dom === "*" && dow === "*" && validMin && validHour) return `Every day at ${at}`;
    // weekly: m h * * d
    if (dom === "*" && /^[0-6]$/.test(dow) && validMin && validHour)
      return `Every ${WEEKDAYS[Number(dow)]} at ${at}`;
    // monthly: m h dom * *
    if (dow === "*" && /^\d+$/.test(dom) && validMin && validHour)
      return `Monthly on day ${Number(dom)} at ${at}`;
  }
  return cron;
}

// Clean 12-hour time picker built from styled <select>s so it matches the rest
// of the form (the native <input type="time"> renders an ugly browser dropdown).
// Value is round-tripped as a 24h "HH:MM" string.
function TimeSelect({ value, onChange }: { value: string; onChange: (hhmm: string) => void }) {
  const [h24, m] = value.split(":").map(Number);
  const isPm = h24 >= 12;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;

  const emit = (nextH12: number, nextMin: number, pm: boolean) => {
    let hh = nextH12 % 12; // 12 -> 0
    if (pm) hh += 12; // PM shifts by 12 (12 PM stays 12, 12 AM -> 0)
    onChange(`${pad2(hh)}:${pad2(nextMin)}`);
  };

  return (
    <div className="row" style={{ gap: 6 }}>
      <Select value={h12} onChange={(e) => emit(Number(e.currentTarget.value), m, isPm)}>
        {Array.from({ length: 12 }, (_, i) => i + 1).map((hr) => (
          <option key={hr} value={hr}>
            {pad2(hr)}
          </option>
        ))}
      </Select>
      <Select value={m} onChange={(e) => emit(h12, Number(e.currentTarget.value), isPm)}>
        {Array.from({ length: 12 }, (_, i) => i * 5).map((mm) => (
          <option key={mm} value={mm}>
            {pad2(mm)}
          </option>
        ))}
      </Select>
      <Select value={isPm ? "PM" : "AM"} onChange={(e) => emit(h12, m, e.currentTarget.value === "PM")}>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </Select>
    </div>
  );
}

type Frequency = "hourly" | "daily" | "weekly" | "monthly";

// Stateful builder that emits a cron string + plain-English preview. Reports
// changes upward via onChange so the parent owns the value it POSTs.
function ScheduleBuilder({ onChange }: { onChange: (cron: string) => void }) {
  const [advanced, setAdvanced] = useState(false);
  const [freq, setFreq] = useState<Frequency>("daily");
  const [minute, setMinute] = useState(0); // hourly: which minute past the hour
  const [time, setTime] = useState("09:00"); // daily/weekly/monthly clock time
  const [weekday, setWeekday] = useState(1); // weekly: 0=Sun ... 6=Sat
  const [dayOfMonth, setDayOfMonth] = useState(1); // monthly: 1..28
  const [rawCron, setRawCron] = useState("0 9 * * *");

  const cron = useMemo(() => {
    if (advanced) return rawCron.trim();
    const [hh, mm] = time.split(":").map(Number);
    switch (freq) {
      case "hourly":
        return `${minute} * * * *`;
      case "daily":
        return `${mm} ${hh} * * *`;
      case "weekly":
        return `${mm} ${hh} * * ${weekday}`;
      case "monthly":
        return `${mm} ${hh} ${dayOfMonth} * *`;
    }
  }, [advanced, rawCron, freq, minute, time, weekday, dayOfMonth]);

  useEffect(() => {
    onChange(cron);
  }, [cron, onChange]);

  return (
    <div className="col" style={{ gap: 10 }}>
      {!advanced ? (
        <div className="row" style={{ gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div className="field" style={{ minWidth: 140 }}>
            <label className="label">Repeat</label>
            <Select value={freq} onChange={(e) => setFreq(e.currentTarget.value as Frequency)}>
              <option value="hourly">Every hour</option>
              <option value="daily">Every day</option>
              <option value="weekly">Every week</option>
              <option value="monthly">Every month</option>
            </Select>
          </div>

          {freq === "weekly" && (
            <div className="field" style={{ minWidth: 130 }}>
              <label className="label">On</label>
              <Select value={weekday} onChange={(e) => setWeekday(Number(e.currentTarget.value))}>
                {WEEKDAYS.map((d, i) => (
                  <option key={i} value={i}>
                    {d}
                  </option>
                ))}
              </Select>
            </div>
          )}

          {freq === "monthly" && (
            <div className="field" style={{ minWidth: 110 }}>
              <label className="label">Day of month</label>
              <Select
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(Number(e.currentTarget.value))}
              >
                {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </Select>
            </div>
          )}

          {freq === "hourly" ? (
            <div className="field" style={{ minWidth: 120 }}>
              <label className="label">At minute</label>
              <Select value={minute} onChange={(e) => setMinute(Number(e.currentTarget.value))}>
                {[0, 5, 10, 15, 20, 30, 45].map((m) => (
                  <option key={m} value={m}>
                    :{pad2(m)}
                  </option>
                ))}
              </Select>
            </div>
          ) : (
            <div className="field">
              <label className="label">At time</label>
              <TimeSelect value={time} onChange={setTime} />
            </div>
          )}
        </div>
      ) : (
        <div className="field">
          <label className="label">Cron expression</label>
          <Input
            className="mono"
            placeholder="0 9 * * *"
            value={rawCron}
            onChange={(e) => setRawCron(e.currentTarget.value)}
          />
        </div>
      )}

      <Row gap="sm">
        <span className="faint" style={{ fontSize: 12 }}>
          {cron.trim() ? humanizeCron(cron) : "-"}
        </span>
        <code className="faint mono" style={{ fontSize: 11 }}>
          ({cron.trim() || "?"})
        </code>
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={() => setAdvanced((a) => !a)}>
          {advanced ? "Simple" : "Advanced (cron)"}
        </Button>
      </Row>
    </div>
  );
}

// -- schedules sub-section (only for existing jobs) ---------------------------
function SchedulesSection({ jobId }: { jobId: string }) {
  const qc = useQueryClient();
  const notify = useToast();
  const [cron, setCron] = useState("0 9 * * *");
  const [timezone, setTimezone] = useState("Asia/Kolkata");
  // The schedule builder is a *draft* form, not the job's current state: it only
  // appears once the operator opts into adding a schedule, so an unscheduled job
  // never looks scheduled.
  const [adding, setAdding] = useState(false);
  // Inline confirm state: which single schedule is being removed, and whether the
  // "clear all" action is awaiting confirmation.
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [confirmingClear, setConfirmingClear] = useState(false);

  const schedulesQuery = useListSchedulesV1JobsJobIdSchedulesGet(jobId);

  const invalidate = () =>
    qc.invalidateQueries({
      queryKey: getListSchedulesV1JobsJobIdSchedulesGetQueryKey(jobId),
    });

  const create = useCreateScheduleV1JobsJobIdSchedulesPost<Error>({
    mutation: {
      onSuccess: () => {
        notify("Schedule attached");
        setCron("0 9 * * *");
        setAdding(false);
        invalidate();
      },
      onError: (e: Error) => notify(e.message, "crit"),
    },
  });

  const toggle = useUpdateScheduleV1SchedulesScheduleIdPatch<Error>({
    mutation: {
      onSuccess: () => invalidate(),
      onError: (e: Error) => notify(e.message, "crit"),
    },
  });

  // Per-row remove and the "clear all" action share this mutation; toasts are
  // fired by the call sites so a bulk clear shows one message, not one per row.
  const remove = useDeleteScheduleV1SchedulesScheduleIdDelete<Error>({
    mutation: {
      onSuccess: () => invalidate(),
      onError: (e: Error) => notify(e.message, "crit"),
    },
  });

  const schedules = schedulesQuery.data ?? [];

  const removeOne = (scheduleId: string) => {
    setConfirmingId(null);
    remove.mutateAsync({ scheduleId }).then(() => notify("Schedule removed")).catch(() => {});
  };

  const clearAll = () => {
    setConfirmingClear(false);
    Promise.all(schedules.map((s) => remove.mutateAsync({ scheduleId: s.id })))
      .then(() => notify("Schedule cleared: job runs manually"))
      .catch(() => {});
  };

  return (
    <div className="field">
      <label className="label">Schedules</label>

      {schedulesQuery.isLoading ? (
        <span className="hint">Loading...</span>
      ) : schedules.length > 0 ? (
        <Stack gap="sm" style={{ marginBottom: 10 }}>
          {schedules.map((s) => (
            <div
              key={s.id}
              className="row"
              style={{
                gap: 10,
                padding: "8px 10px",
                border: "1px solid var(--line)",
                borderRadius: "var(--r)",
              }}
            >
              <span style={{ fontSize: 12.5, color: "var(--ink)" }}>{humanizeCron(s.cron)}</span>
              <code className="faint mono" style={{ fontSize: 10.5 }}>{s.cron}</code>
              <span className="faint mono" style={{ fontSize: 11 }}>{s.timezone}</span>
              {s.next_fire_at && (
                <span className="faint mono" style={{ fontSize: 10.5 }}>
                  next {fmtTime(s.next_fire_at)}
                </span>
              )}
              <div className="flex-1" />
              <Switch
                checked={s.enabled}
                disabled={toggle.isPending}
                onCheckedChange={() =>
                  toggle.mutate({
                    scheduleId: s.id,
                    data: { enabled: !s.enabled },
                  })
                }
                aria-label="toggle schedule"
              />
              {confirmingId === s.id ? (
                <span className="row" style={{ gap: 4, alignItems: "center" }}>
                  <span className="faint" style={{ fontSize: 10.5 }}>Remove?</span>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={remove.isPending}
                    onClick={() => removeOne(s.id)}
                  >
                    Yes
                  </Button>
                  <Button variant="outline" size="sm" disabled={remove.isPending} onClick={() => setConfirmingId(null)}>
                    No
                  </Button>
                </span>
              ) : (
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={remove.isPending}
                  onClick={() => setConfirmingId(s.id)}
                >
                  Remove
                </Button>
              )}
            </div>
          ))}
        </Stack>
      ) : (
        <div
          className="hint"
          style={{
            padding: "8px 10px",
            border: "1px dashed var(--line)",
            borderRadius: "var(--r)",
            marginBottom: 10,
          }}
        >
          No schedule, this job runs only when triggered manually.
        </div>
      )}

      {adding ? (
        <div
          style={{
            padding: "12px",
            border: "1px solid var(--line)",
            borderRadius: "var(--r)",
            marginTop: 4,
          }}
        >
          <label className="label" style={{ marginBottom: 8 }}>
            Add a schedule
          </label>
          <ScheduleBuilder onChange={setCron} />
          <div
            className="row"
            style={{
              gap: 10,
              flexWrap: "wrap",
              alignItems: "flex-end",
              marginTop: 10,
            }}
          >
            <div className="field" style={{ flex: 1, minWidth: 150 }}>
              <label className="label">Timezone</label>
              <Select value={timezone} onChange={(e) => setTimezone(e.currentTarget.value)}>
                <option>Asia/Kolkata</option>
                <option>UTC</option>
              </Select>
            </div>
            <Button
              variant="primary"
              disabled={create.isPending || cron.trim().length === 0}
              onClick={() =>
                create.mutate({
                  jobId,
                  data: { cron: cron.trim(), timezone, enabled: true },
                })
              }
            >
              {create.isPending ? "Adding..." : "Add schedule"}
            </Button>
            <Button variant="outline" disabled={create.isPending} onClick={() => setAdding(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Row gap="sm">
          <Button size="sm" onClick={() => setAdding(true)}>
            + Add schedule
          </Button>
          {schedules.length > 0 &&
            (confirmingClear ? (
              <span className="row" style={{ gap: 4, alignItems: "center" }}>
                <span className="faint" style={{ fontSize: 10.5 }}>Clear all schedules?</span>
                <Button variant="destructive" size="sm" disabled={remove.isPending} onClick={clearAll}>
                  Yes, run manually
                </Button>
                <Button variant="outline" size="sm" disabled={remove.isPending} onClick={() => setConfirmingClear(false)}>
                  Cancel
                </Button>
              </span>
            ) : (
              <Button variant="outline" size="sm" disabled={remove.isPending} onClick={() => setConfirmingClear(true)}>
                Clear schedule (run manually)
              </Button>
            ))}
        </Row>
      )}
    </div>
  );
}

// -- reusable centered modal shell --------------------------------------------
function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 40,
        background: "rgba(0,0,0,.45)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "48px 16px",
        overflowY: "auto",
      }}
      onClick={onClose}
    >
      <Panel
        style={{ width: "100%", maxWidth: 720 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Panel.Head title={title}>
          <div className="flex-1" />
          <Button variant="outline" size="sm" icon onClick={onClose} aria-label="close">
            <X size={14} aria-hidden />
          </Button>
        </Panel.Head>
        {children}
      </Panel>
    </div>
  );
}
